<template>
  <div class="pos-right">
    <form @submit.prevent class="pb-60">
      <div class="row gx-xxl-6 px-3 pt-2">
        <div class="col-lg-6">
          <div class="form-group mb-20">
            <label class="d-block text-secondary fw-bold text-uppercase mb-2" style="font-size: 11px; letter-spacing: 0.5px;">{{ $t("pos.customer") }}</label>
            <select 
              class="form-select bg-white border rounded-pill fs-13 py-2 text-dark shadow-sm"
              v-model="selectedCustomerId"
            >
              <option :value="null">{{ $t("pos.walk_in_customer") }}</option>
              <option v-for="c in customers" :key="c.id" :value="c.id">
                {{ c.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group mb-20">
            <label class="d-block text-secondary fw-bold text-uppercase mb-2" style="font-size: 11px; letter-spacing: 0.5px;">{{ $t("pos.biller") }}</label>
            <select 
              class="form-select bg-white border rounded-pill fs-13 py-2 text-dark shadow-sm"
              v-model="selectedBillerId"
            >
              <option :value="null">{{ $t("pos.choose_biller") }}</option>
              <option v-for="b in billers" :key="b.id" :value="b.id">
                {{ b.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="card border-0 shadow-none rounded-1 mb-3 bg-transparent">
        <SelectedProducts />
      </div>
      
      <div class="total-summary px-xl-4 px-3 border-top pt-4">
        <!-- Text Summaries -->
        <div v-if="activePromotion" class="promotion-auto-card mb-3">
          <div>
            <span class="promotion-auto-kicker">{{ $t("pos.auto_promotion") }}</span>
            <strong>{{ activePromotion.name }}</strong>
            <small>{{ activePromotionSummary }}</small>
          </div>
          <div class="promotion-auto-value">
            {{ activePromotionValueLabel }}
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fs-13 text-secondary fw-semibold">{{ $t("pos.total_item") }} ({{ totalItems }})</span>
          <span class="fs-14 fw-bold text-dark">{{ stateStore.formatPrice(subTotal) }}</span>
        </div>
        
        <div
          class="d-flex justify-content-between align-items-center mb-2 cursor-pointer dropdown-toggle-container"
          :class="{ 'locked-discount-row': !!activePromotion }"
          :data-bs-toggle="activePromotion ? null : 'modal'"
          :data-bs-target="activePromotion ? null : '#discountModal'"
          @click="handleDiscountRowClick"
        >
          <span class="fs-13 text-secondary fw-semibold d-flex align-items-center gap-2">
            {{ activePromotion ? activePromotion.name : $t('pos.discount') }}
            <i v-if="!activePromotion" class="ri-edit-line text-primary"></i>
            <small v-else class="auto-pill">{{ $t("pos.auto_promotion") }}</small>
          </span>
          <span class="fs-14 fw-bold text-danger">
            {{ discountType === 'percentage' ? '-' + discount + '%' : discount > 0 ? '-' + stateStore.formatPrice(discount) : '-' + stateStore.formatPrice(0) }}
          </span>
        </div>
        
        <div class="d-flex justify-content-between align-items-center mb-2 cursor-pointer dropdown-toggle-container" data-bs-toggle="modal" data-bs-target="#couponModal" @click="openCouponModal">
          <span class="fs-13 text-secondary fw-semibold d-flex align-items-center gap-2">{{ $t("pos.coupon") }} <i class="ri-edit-line text-primary"></i></span>
          <span class="fs-14 fw-bold text-danger">
            {{ couponDiscount > 0 ? '-' + stateStore.formatPrice(couponDiscount) : '-' + stateStore.formatPrice(0) }}
          </span>
        </div>
        
        <div class="d-flex justify-content-between align-items-center mb-2 cursor-pointer dropdown-toggle-container" data-bs-toggle="modal" data-bs-target="#taxModal" @click="openTaxModal">
          <span class="fs-13 text-secondary fw-semibold d-flex align-items-center gap-2">{{ $t("pos.tax") }} ({{ taxRate > 0 ? taxRate + '%' : '0%' }}) <i class="ri-edit-line text-primary"></i></span>
          <span class="fs-14 fw-bold text-dark">{{ stateStore.formatPrice(orderTax) }}</span>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-3 cursor-pointer dropdown-toggle-container" data-bs-toggle="modal" data-bs-target="#shippingModal" @click="openShippingModal">
          <span class="fs-13 text-secondary fw-semibold d-flex align-items-center gap-2">{{ $t("pos.shipping") }} <i class="ri-edit-line text-primary"></i></span>
          <span class="fs-14 fw-bold text-dark">{{ stateStore.formatPrice(shippingCost) }}</span>
        </div>

        <!-- Grand Total Block -->
        <div class="grand-total-box bg-primary-subtle rounded-4 p-3 d-flex justify-content-between align-items-center mb-4 mt-2">
          <span class="text-primary fw-bold text-uppercase fs-14">{{ $t("pos.grand_total") }}</span>
          <span class="text-primary fw-bolder" style="font-size: 26px;">{{ stateStore.formatPrice(totalCost) }}</span>
        </div>

        <!-- Payment Buttons Grid -->
        <div class="payment-grid mb-4">
          <button
            v-if="canUseMembershipCard"
            type="button"
            class="payment-btn"
            @click="prepareCheckout('card')"
            data-bs-toggle="modal"
            data-bs-target="#checkoutConfirmModal"
          >
            <i class="ri-bank-card-line text-primary fs-24 mb-1"></i>
            <span>{{ $t("pos.card") }}</span>
          </button>
          <button type="button" class="payment-btn" @click="prepareCheckout('cash')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-money-dollar-circle-line text-primary fs-24 mb-1"></i>
            <span>{{ $t("pos.cash") }}</span>
          </button>
          <!-- <button type="button" class="payment-btn" @click="prepareCheckout('aba')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-qr-code-line text-primary fs-24 mb-1"></i>
            <span>ABA</span>
          </button> -->
          <!-- <button type="button" class="payment-btn" @click="prepareCheckout('wing')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-refresh-line text-primary fs-24 mb-1"></i>
            <span>WING</span>
          </button> -->
          
          <button type="button" class="payment-btn" @click="prepareCheckout('khqr')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-qr-scan-2-line text-primary fs-24 mb-1"></i>
            <span>KHQR</span>
          </button>
          <!-- <button type="button" class="payment-btn" @click="prepareCheckout('aclida')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-bank-line text-primary fs-24 mb-1"></i>
            <span>ACLIDA</span>
          </button> -->
          <!-- <button type="button" class="payment-btn" @click="prepareCheckout('gift_card')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-gift-line text-primary fs-24 mb-1"></i>
            <span>GIFT</span>
          </button> -->
          <button type="button" class="payment-btn pay-btn bg-primary border-primary justify-content-center align-items-center" @click="prepareCheckout('cash')" data-bs-toggle="modal" data-bs-target="#checkoutConfirmModal">
            <i class="ri-shopping-cart-2-line text-white fs-24 mb-1"></i>
            <span class="text-white">{{ $t("pos.pay") }}</span>
          </button>
        </div>

        <!-- Action Buttons Grid -->
        <div class="action-grid pb-2">
          <button type="button" class="action-btn text-danger bg-danger-subtle" @click="handleReset">
            <i class="ri-restart-line me-2 fs-16"></i> {{ $t("pos.reset") }}
          </button>
          <button type="button" class="action-btn text-warning" style="background-color: #fff8e1;" @click="handleSuspend">
            <i class="ri-pause-line me-2 fs-16"></i> {{ $t("pos.suspend") }}
          </button>
          <button type="button" class="action-btn text-dark bg-light border" data-bs-toggle="modal" data-bs-target="#suspendedSalesModal" @click="fetchSuspendedSales">
            <i class="ri-list-check me-2 fs-16"></i> {{ $t("pos.list") }}
          </button>
        </div>

        <button
          type="button"
          class="bill-launch-btn mt-3"
          data-bs-toggle="modal"
          data-bs-target="#invoiceModal"
          :disabled="!lastSaleData"
        >
          <span class="bill-launch-copy">
            <strong>{{ $t("pos.view_bill") }}</strong>
            <small>
              {{ lastSaleData ? $t('pos.latest_reference', { reference: lastSaleData.reference_no }) : $t('pos.complete_sale_to_open_latest_bill') }}
            </small>
          </span>
          <i class="ri-receipt-line fs-20"></i>
        </button>
      </div>
    </form>

    <!-- Hidden button to trigger invoice modal safely -->
    <button id="openInvoiceBtn" type="button" class="d-none" data-bs-toggle="modal" data-bs-target="#invoiceModal"></button>

    <!-- Suspended Sales Modal -->
    <div class="modal fade" id="suspendedSalesModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.suspended_sales") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>{{ $t("pos.date") }}</th>
                    <th>{{ $t("pos.reference") }}</th>
                    <th>{{ $t("pos.customer") }}</th>
                    <th>{{ $t("pos.items") }}</th>
                    <th class="text-end">{{ $t("pos.total") }}</th>
                    <th class="text-end">{{ $t("common.action") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sale in suspendedSales" :key="sale.id">
                    <td>{{ new Date(sale.created_at).toLocaleDateString() }}</td>
                    <td>{{ sale.reference_no }}</td>
                    <td>{{ sale.customer_name || sale.customer?.name || $t('pos.walk_in') }}</td>
                    <td>{{ sale.items?.length || 0 }}</td>
                    <td class="text-end">{{ stateStore.formatPrice(Number(sale.total || 0)) }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-primary" @click="resumeSuspend(sale)" data-bs-dismiss="modal">
                        {{ $t("pos.resume") }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="suspendedSales.length === 0">
                    <td colspan="6" class="text-center py-4 text-muted">{{ $t("pos.no_suspended_sales_found") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Discount Modal -->
    <div class="modal fade" id="discountModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.apply_discount") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="form-group mb-3">
              <label class="form-label fw-bold">{{ $t("pos.discount_type") }}</label>
              <select class="form-select border border-secondary border-opacity-25" v-model="tempDiscountType">
                <option value="fixed">{{ $t("pos.fixed_amount") }}</option>
                <option value="percentage">{{ $t("pos.percentage") }}</option>
              </select>
            </div>
            <div class="form-group mb-3">
              <label class="form-label fw-bold">{{ $t("pos.discount_value") }}</label>
              <input type="number" class="form-control border border-secondary border-opacity-25" v-model="tempDiscount" min="0" step="0.01">
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t("common.cancel") }}</button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyDiscount">{{ $t("common.apply") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Coupon Modal -->
    <div class="modal fade" id="couponModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.apply_coupon") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="form-group mb-3">
              <label class="form-label fw-bold">{{ $t("pos.select_coupon") }}</label>
              <select class="form-select border-0 shadow-sm rounded-pill" v-model="tempCouponCode" @change="handleCouponSelection">
                <option :value="null">{{ $t("pos.select_coupon_placeholder") }}</option>
                <option v-for="coupon in coupons" :key="coupon.id" :value="coupon.code">
                  {{ coupon.name }} ({{ coupon.code }})
                </option>
              </select>
            </div>
            <div v-if="couponMessage" class="alert mt-3" :class="isCouponValid ? 'alert-success' : 'alert-danger'">
              {{ couponMessage }}
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-danger" @click="removeCoupon">{{ $t("pos.remove_coupon") }}</button>
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t("common.close") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tax Modal -->
    <div class="modal fade" id="taxModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.order_tax") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="form-group mb-3">
              <label class="form-label fw-bold">{{ $t("pos.tax_rate") }}</label>
              <input type="number" class="form-control border border-secondary border-opacity-25" v-model="tempTaxRate" min="0" step="0.01">
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t("common.cancel") }}</button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyTax">{{ $t("common.apply") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Shipping Modal -->
    <div class="modal fade" id="shippingModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.shipping_cost") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="form-group mb-3">
              <label class="form-label fw-bold">{{ $t("pos.shipping_amount") }}</label>
              <input type="number" class="form-control border border-secondary border-opacity-25" v-model="tempShippingCost" min="0" step="0.01">
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t("common.cancel") }}</button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyShipping">{{ $t("common.apply") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout Confirmation Modal -->
    <div class="modal fade" id="checkoutConfirmModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
          <div class="modal-header bg-light border-0 px-4 py-3 position-relative">
            <h5 class="modal-title fw-bold text-dark d-flex align-items-center gap-2">
              <div class="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style="width: 32px; height: 32px;">
                <i class="ri-secure-payment-line fs-18"></i>
              </div>
              {{ $t("pos.checkout_confirmation") }}
            </h5>
            <button type="button" class="btn-close bg-secondary bg-opacity-10 rounded-circle p-2" data-bs-dismiss="modal" aria-label="Close" @click="cancelCheckout"></button>
          </div>
          
          <div class="modal-body p-0">
            <div class="row g-0">
              <!-- Left Side: Order Summary -->
              <div class="col-md-5 bg-light border-end p-4 d-flex flex-column h-100">
                <h6 class="fw-bold text-secondary text-uppercase mb-3 fs-12" style="letter-spacing: 1px;">{{ $t("pos.order_summary") }}</h6>
                
                <div class="table-responsive flex-grow-1" style="max-height: 280px;">
                  <table class="table table-borderless table-sm mb-0">
                    <tbody>
                      <tr v-for="item in stateStore.selectedProducts" :key="item.id">
                        <td class="text-dark fw-medium px-0 py-2">
                          <div class="text-truncate" style="max-width: 160px;">{{ item.name }}</div>
                          <div class="text-muted fs-12">{{ stateStore.formatPrice(item.price) }} &times; {{ item.qty }}</div>
                          <div
                            v-if="getProductPromotionLabel(item)"
                            class="text-warning-emphasis fs-11 fw-semibold mt-1"
                          >
                            {{ getProductPromotionLabel(item) }}
                          </div>
                        </td>
                        <td class="text-end fw-bold text-dark px-0 py-2 align-middle fs-14">
                          {{ stateStore.formatPrice(getProductLineTotal(item)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div class="border-top pt-3 mt-3">
                  <div class="d-flex justify-content-between mb-2 text-muted fs-13">
                    <span>{{ $t("pos.items") }} ({{ totalItems }})</span>
                    <span>{{ stateStore.formatPrice(subTotal) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 text-muted fs-13" v-if="discount > 0">
                    <span>{{ $t("pos.discount") }}</span>
                    <span class="text-danger">-{{ discountType === 'percentage' ? discount + '%' : stateStore.formatPrice(discount) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 text-muted fs-13" v-if="orderTax > 0">
                    <span>{{ $t("pos.tax") }} ({{ taxRate }}%)</span>
                    <span>+{{ stateStore.formatPrice(orderTax) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 text-muted fs-13" v-if="shippingCost > 0">
                    <span>{{ $t("pos.shipping") }}</span>
                    <span>+{{ stateStore.formatPrice(shippingCost) }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top border-dark border-opacity-10">
                    <span class="fw-bold text-dark">{{ $t("pos.total") }}</span>
                    <span class="fw-bolder text-primary fs-20">{{ stateStore.formatPrice(totalCost) }}</span>
                  </div>
                </div>
              </div>

              <!-- Right Side: Payment Details -->
              <div class="col-md-7 p-4 bg-white">
                <div class="d-flex align-items-center justify-content-between mb-4">
                  <h6 class="fw-bold text-dark mb-0 fs-15">{{ $t("pos.payment_details") }}</h6>
                  <span class="badge bg-primary-subtle text-primary border border-primary border-opacity-25 px-3 py-2 rounded-pill fw-bold text-uppercase d-flex align-items-center gap-1 shadow-sm">
                    <i class="ri-wallet-3-fill fs-14"></i> {{ activePaymentMethodLabel }}
                  </span>
                </div>
                
                <!-- Payment Amount Input -->
                <div class="mb-4 position-relative">
                  <label class="form-label text-secondary fw-bold fs-12 text-uppercase mb-2" style="letter-spacing: 0.5px;">
                    {{ pendingPaymentMethod === 'cash' ? $t('pos.amount_received') : $t('pos.payment_amount') }}
                  </label>
                  <div class="position-relative d-flex align-items-center">
                    <div class="position-absolute ms-3 z-1">
                      <span class="text-primary fw-bolder fs-20">{{ stateStore.currencySymbol }}</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control form-control-lg border-2 fw-bolder text-dark ps-5 shadow-none rounded-3" 
                      style="font-size: 28px; height: 64px; background-color: #f8fafc; transition: all 0.2s; border-color: #e2e8f0;"
                      v-model="cashReceived"
                      placeholder="0.00"
                      step="0.01"
                      :disabled="pendingPaymentMethod !== 'cash'"
                      @focus="($event.target as HTMLInputElement).select()"
                      onfocus="this.style.borderColor='#3b82f6'; this.style.backgroundColor='#fff'; this.style.boxShadow='0 0 0 0.25rem rgba(59, 130, 246, 0.1)'"
                      onblur="this.style.borderColor='#e2e8f0'; this.style.backgroundColor='#f8fafc'; this.style.boxShadow='none'"
                    >
                  </div>
                </div>

                <div v-if="pendingPaymentMethod === 'card'" class="membership-card-panel mb-4">
                  <label class="form-label text-secondary fw-bold fs-12 text-uppercase mb-2" style="letter-spacing: 0.5px;">
                    {{ $t("pos.membership_card") }}
                  </label>
                  <a-select
                    v-model:value="selectedMembershipCardValue"
                    class="membership-card-select"
                    size="large"
                    show-search
                    allow-clear
                    :options="membershipCardOptions"
                    option-filter-prop="label"
                    :disabled="!availableMembershipCards.length"
                    :placeholder="$t('pos.membership_card_search_placeholder')"
                    :get-popup-container="getMembershipCardPopupContainer"
                  />

                  <div
                    v-if="selectedMembershipCard"
                    class="membership-card-summary mt-3"
                  >
                    <div class="membership-card-summary-row">
                      <span>{{ $t("pos.card_number") }}</span>
                      <strong>{{ selectedMembershipCard.card_number }}</strong>
                    </div>
                    <div class="membership-card-summary-row">
                      <span>{{ $t("pos.customer") }}</span>
                      <strong>{{ selectedMembershipCard.customer_name || selectedMembershipCard.customer?.name || $t('pos.general_walk_in_customer') }}</strong>
                    </div>
                    <div class="membership-card-summary-row">
                      <span>{{ $t("pos.available_balance") }}</span>
                      <strong>{{ stateStore.formatPrice(Number(selectedMembershipCard.balance || 0)) }}</strong>
                    </div>
                    <div class="membership-card-summary-row">
                      <span>{{ $t("pos.balance_after_payment") }}</span>
                      <strong>{{ stateStore.formatPrice(membershipCardRemainingBalance) }}</strong>
                    </div>
                  </div>

                  <div
                    v-if="membershipCardError"
                    class="alert alert-danger border-0 py-2 px-3 mt-3 mb-0"
                  >
                    {{ membershipCardError }}
                  </div>
                  <div
                    v-else-if="!availableMembershipCards.length"
                    class="alert alert-warning border-0 py-2 px-3 mt-3 mb-0"
                  >
                    {{ $t("pos.no_active_membership_cards") }}
                  </div>
                </div>
                
                <!-- Quick Amount Buttons -->
                <div v-if="pendingPaymentMethod === 'cash'" class="d-flex flex-wrap gap-2 mb-4">
                  <button 
                    v-for="amount in [1, 5, 10, 20, 50, 100]" 
                    :key="amount"
                    type="button" 
                    class="btn btn-outline-secondary border-opacity-50 btn-sm rounded-pill px-3 fw-bold flex-grow-1"
                    style="transition: background-color 0.2s;"
                    @click="cashReceived = (Number(cashReceived) || 0) + amount"
                  >
                    +{{ stateStore.currencySymbol }}{{ amount }}
                  </button>
                  <button 
                    type="button" 
                    class="btn bg-primary text-white border-0 btn-sm rounded-pill px-3 fw-bold flex-grow-1"
                    style="transition: none;"
                    onmouseover="this.style.backgroundColor='var(--bs-primary)'"
                    onmouseout="this.style.backgroundColor='var(--bs-primary)'"
                    @click="cashReceived = totalCost"
                  >
                    {{ $t("pos.exact") }}
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-light text-danger border-0 btn-sm rounded-pill px-3 fw-bold flex-grow-1 bg-danger-subtle"
                    @click="cashReceived = 0"
                  >
                    {{ $t("pos.clear") }}
                  </button>
                </div>
                <!-- Change Amount Display -->
                <div class="p-3 rounded-4 mb-4" :class="changeAmount > 0 ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'bg-light border'">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-2">
                      <div class="rounded-circle d-flex align-items-center justify-content-center" :class="changeAmount > 0 ? 'bg-success text-white' : 'bg-secondary bg-opacity-25 text-secondary'" style="width: 32px; height: 32px;">
                        <i class="ri-exchange-dollar-line fs-16"></i>
                      </div>
                      <span class="fw-semibold text-dark">{{ $t("pos.change_return") }}</span>
                    </div>
                    <span class="fw-bolder fs-24" :class="changeAmount > 0 ? 'text-success' : 'text-secondary'">
                      {{ stateStore.formatPrice(changeAmount) }}
                    </span>
                  </div>
                </div>

                <div class="d-flex gap-2 mt-4 pt-2">
                  <button type="button" class="btn btn-light btn-lg fw-bold flex-grow-1 border rounded-3" data-bs-dismiss="modal" @click="cancelCheckout">{{ $t("common.cancel") }}</button>
                  <button type="button" class="btn btn-primary btn-lg fw-bold flex-grow-1 shadow-sm d-flex align-items-center justify-content-center gap-2 rounded-3" style="min-width: 60%;" @click="confirmCheckout" :disabled="isCheckoutDisabled">
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm"></span>
                    <i class="ri-checkbox-circle-line fs-20" v-else></i>
                    {{ $t("pos.confirm_and_pay") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Invoice Modal -->
    <div class="modal fade bill-modal" id="invoiceModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content border-0 shadow-lg bill-modal-content">
          <div class="modal-header border-0 bill-modal-header">
            <div>
              <div class="bill-modal-eyebrow">{{ $t("pos.pos_sale_completed") }}</div>
              <h5 class="modal-title fw-bold mb-1">{{ $t("pos.view_bill") }}</h5>
              <p class="bill-modal-caption mb-0">
                {{ $t("pos.review_final_receipt") }}
              </p>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body p-0 bill-modal-body">
            <div v-if="invoiceLoading" class="bill-loading-state">
              <div class="spinner-border text-primary mb-3" role="status"></div>
              <h6 class="fw-bold mb-2">{{ $t("pos.preparing_bill_preview") }}</h6>
              <p class="text-muted mb-0">{{ $t("pos.loading_saved_sale") }}</p>
            </div>

            <div v-else-if="lastSaleData" class="row g-0">
              <div class="col-lg-5 bill-summary-column">
                <div class="bill-summary-wrap">
                  <div class="bill-summary-hero">
                    <span class="bill-summary-label">{{ $t("pos.receipt_reference") }}</span>
                    <h3 class="mb-2">{{ lastSaleData.reference_no }}</h3>
                    <p class="mb-0">
                      {{ $t("pos.sale_saved_successfully") }}
                    </p>
                  </div>

                  <div class="bill-chip-row">
                    <span class="bill-chip" :class="`is-${getBillTone(lastSaleData.status)}`">
                      {{ formatStatusLabel(lastSaleData.status) }}
                    </span>
                    <span class="bill-chip bill-chip-outline" :class="`is-${getBillTone(lastSaleData.payment_status)}`">
                      {{ formatStatusLabel(lastSaleData.payment_status) }}
                    </span>
                  </div>

                  <div class="bill-stat-grid">
                    <div class="bill-stat-card">
                      <span class="bill-stat-title">{{ $t("pos.grand_total") }}</span>
                      <strong>{{ stateStore.formatPrice(lastSaleData.grand_total) }}</strong>
                    </div>
                    <div class="bill-stat-card">
                      <span class="bill-stat-title">{{ $t("pos.items") }}</span>
                      <strong>{{ getBillItemsCount(lastSaleData) }}</strong>
                    </div>
                    <div class="bill-stat-card" v-if="getBillQueueLabel(lastSaleData)">
                      <span class="bill-stat-title">{{ $t("pos.waiting_no") }}</span>
                      <strong>#{{ getBillQueueLabel(lastSaleData) }}</strong>
                    </div>
                    <div class="bill-stat-card">
                      <span class="bill-stat-title">{{ $t("pos.amount_received") }}</span>
                      <strong>{{ stateStore.formatPrice(lastSaleData.cash_received) }}</strong>
                    </div>
                    <div class="bill-stat-card">
                      <span class="bill-stat-title">
                        {{ lastSaleData.due > 0 ? $t('pos.balance_due') : $t('pos.change_return') }}
                      </span>
                      <strong>
                        {{ stateStore.formatPrice(lastSaleData.due > 0 ? lastSaleData.due : lastSaleData.change_amount) }}
                      </strong>
                    </div>
                  </div>

                  <div class="bill-detail-card">
                    <div class="bill-detail-row">
                      <span>{{ $t("pos.customer") }}</span>
                      <strong>{{ lastSaleData.customer_name }}</strong>
                    </div>
                    <div class="bill-detail-row">
                      <span>{{ $t("pos.warehouse") }}</span>
                      <strong>{{ lastSaleData.warehouse_name }}</strong>
                    </div>
                    <div class="bill-detail-row" v-if="lastSaleData.biller_name">
                      <span>{{ $t("pos.biller") }}</span>
                      <strong>{{ lastSaleData.biller_name }}</strong>
                    </div>
                    <div class="bill-detail-row">
                      <span>{{ $t("pos.payment_method") }}</span>
                      <strong>{{ formatPaymentMethod(lastSaleData.paid_by) }}</strong>
                    </div>
                    <div class="bill-detail-row">
                      <span>{{ $t("pos.sale_date") }}</span>
                      <strong>{{ lastSaleData.date }}</strong>
                    </div>
                    <div class="bill-detail-row" v-if="getBillQueueLabel(lastSaleData)">
                      <span>{{ $t("pos.waiting_ticket") }}</span>
                      <strong>
                        #{{ getBillQueueLabel(lastSaleData) }}
                        ·
                        {{ formatStatusLabel(lastSaleData.queue_status || "pending") }}
                      </strong>
                    </div>
                  </div>

                  <div class="bill-note-card" v-if="stateStore.receiptSettings.showNoteToCustomer">
                    <span class="bill-note-label">{{ $t("pos.customer_note") }}</span>
                    <p class="mb-0">{{ stateStore.receiptSettings.noteToCustomer }}</p>
                  </div>
                </div>
              </div>

              <div class="col-lg-7 bill-preview-column">
                <div class="bill-preview-shell">
                  <div class="bill-paper" id="printableInvoice">
                    <div class="bill-paper-accent"></div>

                    <div class="bill-paper-header">
                      <div class="bill-paper-kicker">{{ $t("pos.customer_copy") }}</div>
                      <h2>{{ stateStore.siteName }}</h2>
                      <p
                        v-if="stateStore.receiptSettings.showAddress && stateStore.companyAddress"
                        class="mb-0"
                      >
                        {{ stateStore.companyAddress }}
                      </p>
                      <p
                        v-if="stateStore.receiptSettings.showPhone && stateStore.companyPhone"
                        class="mb-0"
                      >
                        <!-- Tel:  -->
                        {{ stateStore.companyPhone }}
                      </p>
                      <p
                        v-if="stateStore.receiptSettings.showEmail && stateStore.companyEmail"
                        class="mb-0"
                      >
                        {{ stateStore.companyEmail }}
                      </p>
                    </div>

                    <div class="bill-paper-meta">
                      <div class="bill-paper-meta-card">
                        <span class="bill-paper-meta-label">{{ $t("pos.receipt") }}</span>
                        <strong>{{ lastSaleData.reference_no }}</strong>
                        <small>{{ lastSaleData.date }}</small>
                      </div>
                      <div class="bill-paper-meta-card text-lg-end">
                        <span class="bill-paper-meta-label">{{ $t("pos.customer") }}</span>
                        <strong>{{ lastSaleData.customer_name }}</strong>
                        <small>{{ lastSaleData.warehouse_name }}</small>
                      </div>
                    </div>

                    <div
                      v-if="getBillQueueLabel(lastSaleData)"
                      class="bill-paper-reference"
                    >
                      {{ $t("pos.waiting_number") }} #{{ getBillQueueLabel(lastSaleData) }}
                      ·
                      {{ formatStatusLabel(lastSaleData.queue_status || "pending") }}
                    </div>

                    <div
                      v-if="stateStore.receiptSettings.showBarcode"
                      class="bill-paper-reference"
                    >
                      {{ lastSaleData.reference_no }}
                    </div>

                    <div class="table-responsive">
                      <table class="bill-paper-table">
                        <thead>
                          <tr>
                            <th>{{ $t("pos.description") }}</th>
                            <th class="text-center">{{ $t("pos.qty") }}</th>
                            <th class="text-end">{{ $t("pos.price") }}</th>
                            <th class="text-end">{{ $t("pos.total") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in lastSaleData.items" :key="item.id">
                            <td class="fw-semibold">{{ item.product_name }}</td>
                            <td class="text-center">{{ item.quantity }}</td>
                            <td class="text-end">{{ stateStore.formatPrice(item.unit_price) }}</td>
                            <td class="text-end fw-semibold">{{ stateStore.formatPrice(item.total) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="bill-paper-totals">
                      <div class="bill-paper-total-row">
                        <span>{{ $t("pos.subtotal") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.total) }}</strong>
                      </div>
                      <div
                        v-if="stateStore.receiptSettings.showTaxDiscount && getDirectDiscount(lastSaleData) > 0"
                        class="bill-paper-total-row text-danger"
                      >
                        <span>{{ $t("pos.discount") }}</span>
                        <strong>-{{ stateStore.formatPrice(getDirectDiscount(lastSaleData)) }}</strong>
                      </div>
                      <div
                        v-if="stateStore.receiptSettings.showTaxDiscount && lastSaleData.coupon_discount > 0"
                        class="bill-paper-total-row text-danger"
                      >
                        <span>{{ $t("pos.coupon") }}</span>
                        <strong>-{{ stateStore.formatPrice(lastSaleData.coupon_discount) }}</strong>
                      </div>
                      <div
                        v-if="stateStore.receiptSettings.showTaxDiscount && lastSaleData.tax > 0"
                        class="bill-paper-total-row"
                      >
                        <span>{{ $t("pos.order_tax") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.tax) }}</strong>
                      </div>
                      <div
                        v-if="stateStore.receiptSettings.showTaxDiscount && lastSaleData.shipping > 0"
                        class="bill-paper-total-row"
                      >
                        <span>{{ $t("pos.shipping") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.shipping) }}</strong>
                      </div>
                      <div class="bill-paper-total-row is-grand">
                        <span>{{ $t("pos.grand_total") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.grand_total) }}</strong>
                      </div>
                      <div class="bill-paper-total-row">
                        <span>{{ $t("pos.amount_received") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.cash_received) }}</strong>
                      </div>
                      <div class="bill-paper-total-row">
                        <span>{{ $t("pos.change") }}</span>
                        <strong>{{ stateStore.formatPrice(lastSaleData.change_amount) }}</strong>
                      </div>
                      <div class="bill-paper-total-row">
                        <span>{{ $t("pos.payment_method") }}</span>
                        <strong>{{ formatPaymentMethod(lastSaleData.paid_by) }}</strong>
                      </div>
                    </div>

                    <div class="bill-paper-footer">
                      <p
                        v-if="stateStore.receiptSettings.showNoteToCustomer"
                        class="bill-paper-note"
                      >
                        {{ stateStore.receiptSettings.noteToCustomer }}
                      </p>
                      <p class="bill-paper-powered mb-0">{{ $t("pos.powered_by", { name: stateStore.siteName }) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="bill-empty-state">
              <div class="bill-empty-icon">
                <i class="ri-receipt-line"></i>
              </div>
              <h6 class="fw-bold mb-2">{{ $t("pos.no_bill_available_yet") }}</h6>
              <p class="text-muted mb-0">
                {{ $t("pos.complete_pos_sale_to_view_bill") }}
              </p>
            </div>
          </div>

          <div class="modal-footer border-0 bill-modal-footer">
            <button type="button" class="btn btn-light border" data-bs-dismiss="modal">{{ $t("common.close") }}</button>
            <button
              type="button"
              class="btn btn-primary bill-print-btn"
              @click="printInvoice"
              :disabled="invoiceLoading || !lastSaleData"
            >
              <i class="ri-printer-line me-1"></i> {{ $t("pos.print_bill") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Open Register Modal -->
    <div class="modal fade" id="openRegisterModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.open_register_title") }}</h5>
            <button type="button" class="btn-close btn-close-white" id="closeOpenRegisterModal" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="row">
              <div class="col-md-5 border-end">
                <form @submit.prevent="handleOpenRegister">
                  <div class="form-group mb-3">
                    <label class="form-label fw-bold">{{ $t("pos.opening_balance_cash_in_hand") }}</label>
                    <div class="input-group">
                      <span class="input-group-text register-balance-prefix register-balance-prefix-primary fw-bold">$</span>
                      <input 
                        type="number" 
                        step="0.01" 
                        min="0"
                        class="form-control register-balance-input" 
                        v-model.number="openingBalance" 
                        placeholder="0.00" 
                        required
                      >
                    </div>
                    <small class="text-muted">{{ $t("pos.opening_balance_help") }}</small>
                  </div>
                  <button type="submit" class="btn btn-primary w-100 fw-bold py-2 mt-3" :disabled="isRegisterSubmitting">
                    <span v-if="isRegisterSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                    {{ $t("pos.open_register") }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-light w-100 fw-medium py-2 mt-2"
                    data-bs-dismiss="modal"
                    @click="cancelOpenRegister"
                  >
                    {{ $t("common.cancel") }}
                  </button>
                </form>
              </div>
              <div class="col-md-7">
                <h6 class="fw-bold mb-3"><i class="ri-history-line me-1"></i> {{ $t("pos.recent_register_history") }}</h6>
                <div class="table-responsive" style="max-height: 300px;">
                  <table class="table table-sm table-hover fs-13">
                    <thead class="bg-light">
                      <tr>
                        <th>{{ $t("pos.opened_by") }}</th>
                        <th>{{ $t("pos.opened_at") }}</th>
                        <th>{{ $t("pos.closed_at") }}</th>
                        <th class="text-end">{{ $t("pos.balance") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="h in registerHistory" :key="h.id">
                        <td>
                          <span v-if="h.user">
                            {{ (h.user.first_name || '') + ' ' + (h.user.last_name || '') || h.user.username || '-' }}
                          </span>
                          <span v-else>-</span>
                        </td>
                        <td>{{ new Date(h.opened_at).toLocaleString("en-GB", { timeZone: "Asia/Phnom_Penh", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) }}</td>
                        <td>{{ h.closed_at ? new Date(h.closed_at).toLocaleString("en-GB", { timeZone: "Asia/Phnom_Penh", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) : '-' }}</td>
                        <td class="text-end fw-bold text-primary">${{ Number(h.closing_balance).toFixed(2) }}</td>
                      </tr>
                      <tr v-if="registerHistory.length === 0">
                        <td colspan="4" class="text-center text-muted py-3">{{ $t("pos.no_history_found") }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Close Register Modal -->
    <div class="modal fade" id="closeRegisterModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-danger text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("pos.close_register_title") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="handleCloseRegister">
            <div class="modal-body p-4">
              <!-- Summary Section -->
              <div class="bg-light rounded-2 p-3 mb-4">
                <h6 class="fw-bold border-bottom pb-2 mb-3">{{ $t("pos.register_summary") }}</h6>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">{{ $t("pos.opening_balance") }}:</span>
                  <span class="fw-bold text-dark">${{ Number(currentRegister?.opening_balance || 0).toFixed(2) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">{{ $t("pos.cash_payments") }}:</span>
                  <span class="fw-bold text-success">+ ${{ Number(currentRegister?.payments_cash || 0).toFixed(2) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">{{ $t("pos.bank_payments") }}:</span>
                  <span class="fw-bold text-info">+ ${{ Number(currentRegister?.payments_bank || 0).toFixed(2) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2 border-bottom pb-2">
                  <span class="text-muted">{{ $t("pos.total_discount") }}:</span>
                  <span class="fw-bold text-danger">- ${{ Number(currentRegister?.total_discount || 0).toFixed(2) }}</span>
                </div>
                <div class="d-flex justify-content-between mt-2 pt-1">
                  <span class="fw-bold">{{ $t("pos.expected_balance") }}:</span>
                  <span class="fw-bold text-primary fs-16">${{ (Number(currentRegister?.opening_balance || 0) + Number(currentRegister?.payments_cash || 0) + Number(currentRegister?.payments_bank || 0) - Number(currentRegister?.total_discount || 0)).toFixed(2) }}</span>
                </div>
              </div>

              <div class="form-group mb-3">
                <label class="form-label fw-bold">{{ $t("pos.actual_closing_balance") }}</label>
                <div class="input-group">
                  <span class="input-group-text register-balance-prefix register-balance-prefix-danger fw-bold">$</span>
                  <input 
                    type="number" 
                    step="0.01" 
                    class="form-control register-balance-input register-balance-input-danger" 
                    v-model="closingBalance" 
                    placeholder="0.00" 
                    required
                  >
                </div>
              </div>
              <div class="form-group mb-0">
                <label class="form-label fw-bold">{{ $t("pos.note") }}</label>
                <textarea class="form-control" v-model="closeNote" rows="2" :placeholder="$t('pos.optional_notes')"></textarea>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-light fw-medium" data-bs-dismiss="modal">{{ $t("common.cancel") }}</button>
              <button type="submit" class="btn btn-danger fw-bold shadow-sm" :disabled="isRegisterSubmitting">
                <span v-if="isRegisterSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t("pos.close_register_end_shift") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Hidden buttons for modal triggering -->
    <button id="triggerOpenRegisterModal" type="button" class="d-none" data-bs-toggle="modal" data-bs-target="#openRegisterModal"></button>
    <button id="triggerCloseRegisterModal" type="button" class="d-none" data-bs-toggle="modal" data-bs-target="#closeRegisterModal"></button>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import SelectedProducts from "./SelectedProducts.vue";
import stateStore from "@/utils/store";
import customerService, { type Customer } from "@/services/customer";
import billerService, { type Biller } from "@/services/biller";
import membershipCardService, {
  type MembershipCard,
} from "@/services/membershipCard";
import suspendService from "@/services/suspend";
import { getUser } from "@/utils/auth";
import { hasPermission } from "@/utils/permission";
import posService, { type SaleData } from "@/services/pos";
import productService from "@/services/product";
import registerService from "@/services/register";
import settingService from "@/services/setting";
import { sizeService, type Size } from "@/services/size";
import { sugarService, type Sugar } from "@/services/sugar";
import { useRoute, useRouter } from "vue-router";
import couponService from "@/services/coupon";
import promotionService from "@/services/promotion";
import Swal from 'sweetalert2';
import type { Coupon } from "@/services/coupon";
import {
  writeCustomerDisplayState,
  type CustomerDisplayLineItem,
  type CustomerDisplayOrderSnapshot,
  type CustomerDisplayPromotion,
} from "@/utils/posCustomerDisplay";
import {
  calculateLineBaseTotal,
  calculateLineDiscountAmount,
  calculateLineTotal,
  normalizeDiscountType,
} from "@/utils/posPricing";
import { formatQueueLabel } from "@/utils/posQueue";
import {
  formatPromotionValue,
  resolveBestPromotion,
  type ActivePromotion,
  type PromotionRecord,
} from "@/utils/promotion";

interface PosBillItem {
  id: number | string;
  product_name: string;
  quantity: number;
  unit_price: number;
  base_total: number;
  discount_amount: number;
  discount_value?: number;
  discount_type?: "fixed" | "percentage";
  size_id?: number | null;
  sugar_id?: number | null;
  size_name?: string | null;
  sugar_name?: string | null;
  total: number;
}

interface PosBillData {
  id: number;
  reference_no: string;
  customer_name: string;
  warehouse_name: string;
  biller_name: string;
  total: number;
  discount: number;
  total_discount: number;
  coupon_discount: number;
  tax: number;
  shipping: number;
  grand_total: number;
  paid: number;
  due: number;
  status: string;
  payment_status: string;
  paid_by: string;
  date: string;
  created_at: string;
  cash_received: number;
  change_amount: number;
  queue_number?: number | null;
  queue_label?: string | null;
  queue_status?: string | null;
  queue_completed_at?: string | null;
  membership_card_number?: string | null;
  items: PosBillItem[];
  promotion?: CustomerDisplayPromotion | null;
}

interface PosPaymentSnapshot {
  cash_received: number;
  change_amount: number;
}

const LAST_BILL_STORAGE_KEY = "pos_latest_bill";

export default defineComponent({
  name: "PosRightContent",
  components: {
    SelectedProducts,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { t, locale } = useI18n();

    const showError = (msg: string) =>
      Swal.fire({ icon: "error", title: t("pos.error"), text: msg });
    const showSuccess = (msg: string) =>
      Swal.fire({
        icon: "success",
        title: t("pos.success"),
        text: msg,
        timer: 1500,
        showConfirmButton: false,
      });
    const showWarning = (msg: string) =>
      Swal.fire({ icon: "warning", title: t("pos.warning"), text: msg });
    const canUseMembershipCard = hasPermission("membership_card_use");
    const canUsePromotion = hasPermission("promotion_use");

    const customers = ref<Customer[]>([]);
    const billers = ref<Biller[]>([]);
    const membershipCards = ref<MembershipCard[]>([]);
    const coupons = ref<Coupon[]>([]);
    const promotions = ref<PromotionRecord[]>([]);
    const sizes = ref<Size[]>([]);
    const sugars = ref<Sugar[]>([]);
    
    const referenceNo = ref("");
    const selectedCustomerId = ref<number | null>(null);
    const selectedBillerId = ref<number | null>(null);
    const note = ref("");
    const isSubmitting = ref(false);
    const editingSaleId = ref<number | null>(null);
    const pendingPaymentMethod = ref<string | null>(null);
    const invoiceLoading = ref(false);
    const lastSaleData = ref<PosBillData | null>(null);
    const manualDiscount = ref(0);
    const manualDiscountType = ref<"fixed" | "percentage">("fixed");
    const selectedMembershipCardId = ref<number | null>(null);

    const persistLastBill = (bill: PosBillData | null) => {
      lastSaleData.value = bill;

      if (typeof window === "undefined") return;

      try {
        if (bill) {
          window.sessionStorage.setItem(LAST_BILL_STORAGE_KEY, JSON.stringify(bill));
        } else {
          window.sessionStorage.removeItem(LAST_BILL_STORAGE_KEY);
        }
      } catch {
        // Ignore storage failures and keep the in-memory bill available.
      }
    };

    const restoreLastBill = () => {
      if (typeof window === "undefined") return;

      try {
        const storedBill = window.sessionStorage.getItem(LAST_BILL_STORAGE_KEY);
        if (!storedBill) return;

        lastSaleData.value = JSON.parse(storedBill) as PosBillData;
      } catch {
        window.sessionStorage.removeItem(LAST_BILL_STORAGE_KEY);
      }
    };

    const fetchNextReference = async () => {
      if (editingSaleId.value) return;
      try {
        const nextRef = await posService.getNextReference();
        referenceNo.value = nextRef;
      } catch (error) {
        console.error("Error fetching next reference:", error);
        referenceNo.value = "SALE-" + Math.floor(Date.now() / 1000);
      }
    };

    const getWarehouseStock = (product: any, warehouseId: number | null) => {
      if (!warehouseId) {
        return Number(product.qty) || 0;
      }

      const stocks = Array.isArray(product.stocks) ? product.stocks : [];
      return stocks
        .filter((stock: any) => Number(stock.warehouse_id) === Number(warehouseId))
        .reduce((sum: number, stock: any) => sum + Number(stock.qty ?? stock.quantity ?? 0), 0);
    };

    const formatDateTime = (value?: string) => {
      if (!value) return "-";
      const activeLocale = String(locale.value || "en").toLowerCase().startsWith("km")
        ? "km-KH"
        : "en-GB";

      return new Date(value).toLocaleString(activeLocale, {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatusLabel = (value?: string) => {
      const normalized = String(value || "pending")
        .replace(/_/g, " ")
        .trim()
        .toLowerCase();

      const keyMap: Record<string, string> = {
        pending: "status_pending",
        completed: "status_completed",
        paid: "status_paid",
        partial: "status_partial",
        cancelled: "status_cancelled",
        failed: "status_failed",
        void: "status_void",
      };

      const translationKey = keyMap[normalized];
      return translationKey
        ? t(`pos.${translationKey}`)
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const getBillTone = (value?: string) => {
      const normalized = String(value || "pending")
        .replace(/_/g, " ")
        .trim()
        .toLowerCase();

      if (["completed", "paid", "success"].includes(normalized)) return "success";
      if (["partial", "pending"].includes(normalized)) return "warning";
      if (["cancelled", "failed", "void"].includes(normalized)) return "danger";
      return "neutral";
    };

    const formatPaymentMethod = (value?: string) => {
      const normalized = String(value || "cash")
        .replace(/[\s-]+/g, "_")
        .toLowerCase();

      const keyMap: Record<string, string> = {
        cash: "payment_cash",
        card: "payment_card",
        khqr: "payment_khqr",
        aba: "payment_aba",
        aclida: "payment_aclida",
        wing: "payment_wing",
        gift_card: "payment_gift_card",
        bank: "payment_bank",
      };

      const translationKey = keyMap[normalized];
      return translationKey
        ? t(`pos.${translationKey}`)
        : normalized.replace(/_/g, " ").toUpperCase();
    };

    const activePaymentMethodLabel = computed(() =>
      formatPaymentMethod(pendingPaymentMethod.value || "cash")
    );

    const getBillItemsCount = (sale?: PosBillData | null) =>
      (sale?.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const getDirectDiscount = (sale?: PosBillData | null) =>
      Math.max(0, Number(sale?.total_discount || 0) - Number(sale?.coupon_discount || 0));

    const getProductDiscountAmount = (product: {
      price: number;
      qty: number;
      discount?: number;
      discount_type?: string;
    }) =>
      calculateLineDiscountAmount({
        unitPrice: Number(product.price || 0),
        quantity: Number(product.qty || 0),
        discount: Number(product.discount || 0),
        discountType: product.discount_type,
      });

    const getProductLineTotal = (product: {
      price: number;
      qty: number;
      discount?: number;
      discount_type?: string;
    }) =>
      calculateLineTotal({
        unitPrice: Number(product.price || 0),
        quantity: Number(product.qty || 0),
        discount: Number(product.discount || 0),
        discountType: product.discount_type,
      });

    const getProductPromotionLabel = (product: {
      price: number;
      qty: number;
      discount?: number;
      discount_type?: string;
    }) => {
      const discountAmount = getProductDiscountAmount(product);
      if (discountAmount <= 0) return "";

      return normalizeDiscountType(product.discount_type) === "percentage"
        ? t("pos.promo_percentage_off", {
            value: Number(product.discount || 0),
          })
        : t("pos.promo_amount_off", {
            amount: stateStore.formatPrice(discountAmount),
          });
    };

    const resolveSelectedSizeName = (input: {
      size_id?: number | null;
      size_name?: string | null;
      product_sizes?: Array<{
        size_id?: number | null;
        size?: { name?: string } | null;
      }> | null;
    }) => {
      const providedName = String(input.size_name || "").trim();
      if (providedName) {
        return providedName;
      }

      const sizeId = Number(input.size_id || 0);
      if (!sizeId) {
        return "";
      }

      const matchedProductSize = Array.isArray(input.product_sizes)
        ? input.product_sizes.find(
            (productSize) => Number(productSize?.size_id || 0) === sizeId
          )
        : null;
      const embeddedSizeName = String(
        matchedProductSize?.size?.name || ""
      ).trim();

      if (embeddedSizeName) {
        return embeddedSizeName;
      }

      return (
        sizes.value.find((size) => Number(size.id || 0) === sizeId)?.name || ""
      ).trim();
    };

    const resolveSelectedSugarName = (input: {
      sugar_id?: number | null;
      sugar_name?: string | null;
    }) => {
      const providedName = String(input.sugar_name || "").trim();
      if (providedName) {
        return providedName;
      }

      const sugarId = Number(input.sugar_id || 0);
      if (!sugarId) {
        return "";
      }

      return (
        sugars.value.find((sugar) => Number(sugar.id || 0) === sugarId)?.name ||
        ""
      ).trim();
    };

    const normalizeBillData = (
      rawSale: any,
      paymentSnapshot: PosPaymentSnapshot,
      fallback?: Partial<PosBillData>
    ): PosBillData => {
      const items: PosBillItem[] = (rawSale?.items || fallback?.items || []).map(
        (item: any, index: number) => {
          const quantity = Number(item.quantity ?? item.qty ?? 0);
          const unitPrice = Number(item.unit_price ?? item.price ?? 0);
          const baseTotal = calculateLineBaseTotal(unitPrice, quantity);
          const preservedFallbackItem = fallback?.items?.[index];
          const discountAmount = Math.max(
            0,
            Number(
              item.discount ??
                item.discount_amount ??
                preservedFallbackItem?.discount_amount ??
                0
            )
          );
          const discountType = normalizeDiscountType(
            item.discount_type ?? preservedFallbackItem?.discount_type
          );
          const discountValue =
            item.discount_value ??
            preservedFallbackItem?.discount_value ??
            (discountType === "percentage"
              ? Number(item.discount ?? preservedFallbackItem?.discount_amount ?? 0)
              : discountAmount);
          const total = Number(
            item.total ?? item.subtotal ?? Math.max(0, baseTotal - discountAmount)
          );

          return {
            id: item.id ?? `${rawSale?.id ?? fallback?.id ?? "sale"}-${index}`,
            product_name: item.product_name || item.name || t("pos.item"),
            quantity,
            unit_price: unitPrice,
            base_total: baseTotal,
            discount_amount: Math.min(baseTotal, discountAmount),
            discount_value: Number(discountValue || 0),
            discount_type: discountType,
            size_id: item.size_id ?? preservedFallbackItem?.size_id ?? null,
            sugar_id: item.sugar_id ?? preservedFallbackItem?.sugar_id ?? null,
            size_name: resolveSelectedSizeName({
              size_id: item.size_id ?? preservedFallbackItem?.size_id ?? null,
              size_name:
                item.size?.name ??
                item.size_name ??
                preservedFallbackItem?.size_name ??
                "",
            }),
            sugar_name: resolveSelectedSugarName({
              sugar_id: item.sugar_id ?? preservedFallbackItem?.sugar_id ?? null,
              sugar_name:
                item.sugar?.name ??
                item.sugar_name ??
                preservedFallbackItem?.sugar_name ??
                "",
            }),
            total,
          };
        }
      );

      const itemsTotal = items.reduce((sum, item) => sum + Number(item.total || 0), 0);
      const total = Number(rawSale?.total ?? fallback?.total ?? itemsTotal);
      const grandTotal = Number(rawSale?.grand_total ?? fallback?.grand_total ?? total);
      const paid = Number(rawSale?.paid ?? fallback?.paid ?? grandTotal);

      return {
        id: Number(rawSale?.id ?? fallback?.id ?? 0),
        reference_no: String(rawSale?.reference_no ?? fallback?.reference_no ?? "-"),
        customer_name:
          rawSale?.customer_name ||
          rawSale?.customer?.name ||
          fallback?.customer_name ||
          t("pos.walk_in_customer"),
        warehouse_name:
          rawSale?.warehouse_name ||
          rawSale?.warehouse?.name ||
          fallback?.warehouse_name ||
          t("pos.main_warehouse"),
        biller_name:
          rawSale?.biller_name ||
          rawSale?.biller?.name ||
          fallback?.biller_name ||
          "",
        total,
        discount: Number(rawSale?.discount ?? fallback?.discount ?? 0),
        total_discount: Number(rawSale?.total_discount ?? fallback?.total_discount ?? 0),
        coupon_discount: Number(rawSale?.coupon_discount ?? fallback?.coupon_discount ?? 0),
        tax: Number(rawSale?.tax ?? fallback?.tax ?? 0),
        shipping: Number(rawSale?.shipping ?? fallback?.shipping ?? 0),
        grand_total: grandTotal,
        paid,
        due: Math.max(0, grandTotal - paid),
        status: String(rawSale?.status ?? fallback?.status ?? "completed"),
        payment_status: String(
          rawSale?.payment_status ?? fallback?.payment_status ?? (paid >= grandTotal ? "paid" : "partial")
        ),
        paid_by: String(rawSale?.paid_by ?? fallback?.paid_by ?? "cash"),
        date: formatDateTime(rawSale?.date || rawSale?.created_at || fallback?.created_at || fallback?.date),
        created_at: String(rawSale?.created_at ?? fallback?.created_at ?? ""),
        cash_received: Math.max(Number(paymentSnapshot.cash_received || 0), paid),
        change_amount: Math.max(0, Number(paymentSnapshot.change_amount || 0)),
        queue_number: Number(
          rawSale?.queue_number ?? fallback?.queue_number ?? 0
        ) || null,
        queue_label: String(
          rawSale?.queue_label ??
            fallback?.queue_label ??
            formatQueueLabel(rawSale?.queue_number ?? fallback?.queue_number, "")
        ),
        queue_status: String(
          rawSale?.queue_status ?? fallback?.queue_status ?? "pending"
        ),
        queue_completed_at: String(
          rawSale?.queue_completed_at ?? fallback?.queue_completed_at ?? ""
        ),
        membership_card_number: String(
          rawSale?.membership_card_number ??
            fallback?.membership_card_number ??
            ""
        ),
        items,
        promotion:
          rawSale?.promotion || fallback?.promotion
            ? {
                ...((rawSale?.promotion || fallback?.promotion) as CustomerDisplayPromotion),
              }
            : null,
      };
    };

    const buildFallbackBillData = (
      saleData: SaleData,
      paymentSnapshot: PosPaymentSnapshot,
      saleId = 0
    ) =>
      normalizeBillData(
        {
          id: saleId,
          reference_no: saleData.reference_no,
          customer_name:
            customers.value.find((customer) => customer.id === selectedCustomerId.value)?.name ||
            t("pos.walk_in_customer"),
          warehouse_name:
            stateStore.selectedWarehouseName || t("pos.main_warehouse"),
          biller_name:
            billers.value.find((biller) => biller.id === selectedBillerId.value)?.name || "",
          total: stateStore.selectedProducts.reduce(
            (sum, product) => sum + getProductLineTotal(product),
            0
          ),
          discount: Number(saleData.discount || 0),
          total_discount: Number(saleData.total_discount || 0),
          coupon_discount: Number(saleData.coupon_discount || 0),
          tax: Number(saleData.tax || 0),
          shipping: Number(saleData.shipping || 0),
          grand_total: Number(saleData.paid || 0),
          paid: Number(saleData.paid || 0),
          status: saleData.status || "completed",
          payment_status: Number(saleData.paid || 0) > 0 ? "paid" : "pending",
          paid_by: saleData.paid_by || "cash",
          created_at: new Date().toISOString(),
          membership_card_number:
            saleData.membership_card_number ||
            selectedMembershipCard.value?.card_number ||
            "",
          promotion: buildCustomerDisplayPromotion(activePromotion.value),
          items: stateStore.selectedProducts.map((product) => ({
            id: product.id,
            product_name: product.name,
            quantity: Number(product.qty || 0),
            unit_price: Number(product.price || 0),
            base_total: calculateLineBaseTotal(
              Number(product.price || 0),
              Number(product.qty || 0)
            ),
            discount_amount: getProductDiscountAmount(product),
            discount_value: Number(product.discount || 0),
            discount_type: normalizeDiscountType(product.discount_type),
            size_id: product.size_id ?? null,
            sugar_id: product.sugar_id ?? null,
            size_name: resolveSelectedSizeName({
              size_id: product.size_id,
              product_sizes: product.product_sizes,
            }),
            sugar_name: resolveSelectedSugarName({
              sugar_id: product.sugar_id,
            }),
            total: getProductLineTotal(product),
          })),
        },
        paymentSnapshot
      );

    const loadSavedBillData = async (
      saleId: number,
      paymentSnapshot: PosPaymentSnapshot,
      fallbackBill: PosBillData
    ) => {
      persistLastBill(fallbackBill);
      invoiceLoading.value = true;

      try {
        const savedSale = await posService.getSale(saleId);
        persistLastBill(normalizeBillData(savedSale, paymentSnapshot, fallbackBill));
      } catch (error) {
        console.error("Unable to load saved POS sale bill:", error);
      } finally {
        invoiceLoading.value = false;
      }
    };

    const getBillQueueLabel = (sale?: PosBillData | null) =>
      sale
        ? sale.queue_label || formatQueueLabel(sale.queue_number, "")
        : "";

    const availableMembershipCards = computed(() => {
      const customerId = Number(selectedCustomerId.value || 0);

      return membershipCards.value.filter((card) => {
        const isCardActive =
          String(card.status ?? 1) !== "0" &&
          String(card.status ?? 1) !== "false";

        if (!isCardActive) {
          return false;
        }

        if (!customerId) {
          return true;
        }

        const cardCustomerId = Number(card.customer_id || 0);
        return !cardCustomerId || cardCustomerId === customerId;
      });
    });

    const getMembershipCardCustomerLabel = (card: MembershipCard) =>
      card.customer_name ||
      card.customer?.name ||
      t("pos.general_walk_in_customer");

    const membershipCardOptions = computed(() =>
      availableMembershipCards.value
        .map((card) => ({
          value: Number(card.id || 0),
          label: `${card.card_name} • ${card.card_number} • ${getMembershipCardCustomerLabel(card)}`,
        }))
        .filter((card) => card.value > 0)
    );

    const selectedMembershipCardValue = computed<number | undefined>({
      get: () =>
        selectedMembershipCardId.value !== null
          ? Number(selectedMembershipCardId.value)
          : undefined,
      set: (value) => {
        selectedMembershipCardId.value =
          typeof value === "number" ? Number(value) : null;
      },
    });

    const getMembershipCardPopupContainer = (triggerNode: HTMLElement) =>
      triggerNode.parentElement || triggerNode;

    const selectedMembershipCard = computed(() =>
      membershipCards.value.find(
        (card) => Number(card.id || 0) === Number(selectedMembershipCardId.value || 0)
      ) || null
    );

    const membershipCardRemainingBalance = computed(() => {
      const balance = Number(selectedMembershipCard.value?.balance || 0);
      return Math.max(0, balance - Number(totalCost.value || 0));
    });

    const membershipCardError = computed(() => {
      if (pendingPaymentMethod.value !== "card") {
        return "";
      }

      if (!selectedMembershipCard.value) {
        return availableMembershipCards.value.length
          ? t("pos.select_membership_card_to_continue")
          : t("pos.no_active_membership_card_for_customer");
      }

      const card = selectedMembershipCard.value;
      const currentCustomerId = Number(selectedCustomerId.value || 0);

      if (
        String(card.status ?? 1) === "0" ||
        String(card.status ?? 1) === "false"
      ) {
        return t("pos.selected_membership_card_inactive");
      }

      if (card.expiry_date) {
        const expiry = new Date(card.expiry_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!Number.isNaN(expiry.getTime()) && expiry < today) {
          return t("pos.selected_membership_card_expired");
        }
      }

      if (
        currentCustomerId &&
        Number(card.customer_id || 0) &&
        Number(card.customer_id || 0) !== currentCustomerId
      ) {
        return t("pos.selected_membership_card_different_customer");
      }

      if (Number(card.balance || 0) < Number(totalCost.value || 0)) {
        return t("pos.membership_card_insufficient_balance", {
          amount: stateStore.formatPrice(Number(card.balance || 0)),
        });
      }

      return "";
    });

    const isCheckoutDisabled = computed(() => {
      if (isSubmitting.value || !pendingPaymentMethod.value) {
        return true;
      }

      if (pendingPaymentMethod.value === "cash") {
        return Number(cashReceived.value || 0) < Number(totalCost.value || 0);
      }

      if (pendingPaymentMethod.value === "card") {
        return membershipCardError.value.length > 0;
      }

      return false;
    });

    // Discount, Coupon, Tax, Shipping State
    const discount = ref(0);
    const discountType = ref('fixed'); // 'fixed' or 'percentage'
    const couponCode = ref('');
    const couponDiscount = ref(0);
    const couponId = ref<number | null>(null);
    const taxRate = ref(0);
    const shippingCost = ref(0);

    // Cash Received and Change
    const cashReceived = ref(0);
    const changeAmount = computed(() => {
      const amount = cashReceived.value - totalCost.value;
      return amount > 0 ? amount : 0;
    });

    // Temp variables for modals
    const tempDiscount = ref(0);
    const tempDiscountType = ref('fixed');
    const tempCouponCode = ref('');
    const tempTaxRate = ref(0);
    const tempShippingCost = ref(0);
    const couponMessage = ref('');
    const isCouponValid = ref<boolean | null>(null);
    // Register state
    const openingBalance = ref<number | null>(null);
    const closingBalance = ref(0);
    const closeNote = ref("");
    const isRegisterSubmitting = ref(false);
    const currentRegister = ref<any>(null);
    const registerHistory = ref<any[]>([]);

    const checkRegisterStatus = async () => {
      try {
        const res = await registerService.checkRegister();
        stateStore.setRegisterStatus(res.can_sale, res.register_id || null);
        currentRegister.value = res.register;
      } catch (error: any) {
        // If 403, it means register is not open
        if (error.response?.status === 403) {
          stateStore.setRegisterStatus(false, null);
          fetchRegisterHistory();
        } else {
          console.error("Check register error:", error);
        }
      }
    };
    const fetchRegisterHistory = async () => {
      try {
        const res = await registerService.getHistory();
        registerHistory.value = res.data;
      } catch (error) {
        console.error("Fetch history error:", error);
      }
    };

    const suspendedSales = ref<any[]>([]);

    const handleSuspend = async () => {
      const user = getUser();
      if (!user || !user.id) {
        showError(t("pos.user_not_authenticated"));
        return;
      }

      if (stateStore.selectedProducts.length === 0) {
        showWarning(t("pos.please_select_products_first"));
        return;
      }

      if (!stateStore.selectedWarehouseId) {
        showWarning(t("pos.please_select_warehouse_first"));
        return;
      }

      const suspendData: any = {
        user_id: user.id,
        // Use a unique reference for the suspended sale to avoid conflicts
        reference_no: `SUS-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        warehouse_id: stateStore.selectedWarehouseId,
        customer_id: selectedCustomerId.value || null,
        biller_id: selectedBillerId.value || null,
        total: totalCost.value,
        item: stateStore.selectedProducts.length,
        note: "",
        items: stateStore.selectedProducts.map(p => ({
          product_id: p.id,
          product_code: p.code,
          product_name: p.name,
          unit_price: Number(p.price),
          quantity: p.qty,
          warehouse_id: stateStore.selectedWarehouseId!,
          discount: Number(p.discount || 0),
          item_discount: getProductDiscountAmount(p),
          subtotal: getProductLineTotal(p),
          size_id: p.size_id || null,
          sugar_id: p.sugar_id || null
        }))
      };

      try {
        await suspendService.createSuspend(suspendData);
        showSuccess(t("pos.sale_suspended_successfully"));
        stateStore.clearSelectedProducts();
        referenceNo.value = "SALE-" + Math.floor(Date.now() / 1000);
        selectedCustomerId.value = null;
        selectedBillerId.value = null;
      } catch (error: any) {
        console.error("Error suspending sale:", error);
        const errorMsg = error.response?.data?.message || error.message || "Unknown error";
        const validationErrors = error.response?.data?.errors;
        let detailedError = errorMsg;
        
        if (validationErrors) {
          detailedError += "\n" + Object.values(validationErrors).flat().join("\n");
        }
        
        showError(t("pos.failed_to_suspend_sale", { details: detailedError }));
      }
    };

    const fetchSuspendedSales = async () => {
      try {
        const res = await suspendService.getSuspends();
        suspendedSales.value = res.data || res;
      } catch (error) {
        console.error("Error fetching suspended sales:", error);
      }
    };

    const resumeSuspend = async (suspend: any) => {
      // Clear cart
      stateStore.selectedProducts.splice(0, stateStore.selectedProducts.length);
      
      if (suspend.customer_id) selectedCustomerId.value = suspend.customer_id;
      if (suspend.biller_id) selectedBillerId.value = suspend.biller_id;
      if (suspend.warehouse_id && (stateStore as any).setWarehouse) (stateStore as any).setWarehouse(suspend.warehouse_id, suspend.warehouse_name || suspend.warehouse?.name || "");

      if (suspend.items && Array.isArray(suspend.items)) {
        for (const item of suspend.items) {
          try {
             const fullProduct = await productService.getProduct(item.product_id);
             
             // Retrieve the correct price based on the selected size (if any)
             let finalPrice = Number(item.unit_price) || Number(fullProduct.price) || 0;
             if (item.size_id && fullProduct.product_sizes) {
               const matchedSize = fullProduct.product_sizes.find(
                 (ps: any) => Number(ps.size_id) === Number(item.size_id)
               );
               if (matchedSize) {
                 finalPrice = Number(matchedSize.price);
               }
             }

             const product = {
               id: fullProduct.id!,
               code: fullProduct.code,
               name: fullProduct.name,
               price: finalPrice,
               qty: item.quantity,
               stock: fullProduct.qty || 100,
               type: fullProduct.type,
               discount: Number(item.discount || 0),
               discount_type: normalizeDiscountType(item.discount_type),
               size_id: item.size_id || null,
               sugar_id: item.sugar_id || null,
               product_sizes: fullProduct.product_sizes || []
             };
             stateStore.selectedProducts.push(product);
          } catch (e) {
             console.error("Failed to fetch product for suspended item", item, e);
          }
        }
      }
      
      try {
        if (suspend.id) {
            await suspendService.deleteSuspend(suspend.id);
        }
      } catch (error) {
        console.error("Error deleting suspended sale:", error);
      }
    };

    const handleOpenRegister = async () => {
      const parsedOpeningBalance = Number(openingBalance.value);

      if (
        openingBalance.value === null ||
        Number.isNaN(parsedOpeningBalance) ||
        parsedOpeningBalance < 0
      ) {
        showWarning(t("pos.opening_balance_zero_or_greater"));
        return;
      }
      isRegisterSubmitting.value = true;
      try {
        console.log("Attempting to open register with balance:", parsedOpeningBalance);
        const res = await registerService.openRegister(parsedOpeningBalance);
        console.log("Open register API Response:", res);

        // API might return ID in res.register_id or res.data.id
        const registerId = res.register_id || res.data?.id;
        
        if (registerId) {
          console.log("Register ID found:", registerId);
          stateStore.setRegisterStatus(true, registerId, parsedOpeningBalance);
          
          // Force a small delay to ensure backend is ready before checking status
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Refresh register details to sync currentRegister object
          await checkRegisterStatus();
          
          // Close modal using multiple methods to be sure
          const closeBtn = document.getElementById('closeOpenRegisterModal');
          if (closeBtn) {
            closeBtn.click();
          } else {
            // Fallback: trigger hidden close button by class if ID fails
            const fallbackBtn = document.querySelector('#openRegisterModal .btn-close') as HTMLButtonElement;
            if (fallbackBtn) fallbackBtn.click();
          }
          
          // Reset form
          openingBalance.value = null;
          console.log("Register successfully opened and state updated.");
        } else {
          // If we are here, it might be that the register is already open (status 400 from log)
          // Let's try to fetch the status anyway
          console.warn("No register ID in response, but checking status anyway.");
          await checkRegisterStatus();
          
          const closeBtn = document.getElementById('closeOpenRegisterModal');
          if (closeBtn) closeBtn.click();
        }
      } catch (error: any) {
        console.error("Detailed Open Register Error:", error);
        
        // If status is 400, it might mean already open
        if (error.response?.status === 400) {
          console.log("Register might already be open, refreshing status...");
          await checkRegisterStatus();
          const closeBtn = document.getElementById('closeOpenRegisterModal');
          if (closeBtn) closeBtn.click();
        } else {
          showError(
            error.response?.data?.message || t("pos.failed_to_open_register")
          );
        }
      } finally {
        isRegisterSubmitting.value = false;
      }
    };

    const cancelOpenRegister = () => {
      router.push('/');
    };

    const handleCloseRegister = async () => {
      isRegisterSubmitting.value = true;
      try {
        await registerService.closeRegister(closingBalance.value, closeNote.value);
        showSuccess(t("pos.register_closed"));
        stateStore.setRegisterStatus(false, null);
        currentRegister.value = null;
        fetchRegisterHistory();
        // Close modal
        const closeBtn = document.querySelector('#closeRegisterModal .btn-close') as HTMLButtonElement;
        if (closeBtn) closeBtn.click();
        // Redirect or refresh
        router.push('/sales-list');
      } catch (error: any) {
        showError(
          error.response?.data?.message || t("pos.failed_to_close_register")
        );
      } finally {
        isRegisterSubmitting.value = false;
      }
    };

    const fetchInitialData = async () => {
      await checkRegisterStatus();
      await fetchNextReference();
      try {
        if (!canUsePromotion) {
          stateStore.setActivePromotion(null);
        }
        const [cData, bData, membershipCardData, sData, couponData, promotionData] = await Promise.all([
          customerService.getCustomers(),
          billerService.getBillers(),
          canUseMembershipCard
            ? membershipCardService.getAvailableMembershipCards({ active_only: true })
            : Promise.resolve([] as MembershipCard[]),
          settingService.getSettings(),
          couponService.getCoupons(),
          canUsePromotion
            ? promotionService.getAvailablePromotions()
            : Promise.resolve([] as PromotionRecord[]),
        ]);
        customers.value = cData;
        billers.value = bData;
        membershipCards.value = membershipCardData;
        coupons.value = couponData.data.filter((c: any) => c.status);
        promotions.value = promotionData;

        const [sizeResult, sugarResult] = await Promise.allSettled([
          sizeService.getSizes(),
          sugarService.getSugars(),
        ]);

        sizes.value =
          sizeResult.status === "fulfilled" ? sizeResult.value : [];
        sugars.value =
          sugarResult.status === "fulfilled" ? sugarResult.value : [];

        if (sData.default_currency) {
          const symbol = sData.default_currency === "KHR" ? "៛" : "$";
          stateStore.setCurrencySymbol(symbol);
        }

        if (sData.default_customer) {
          selectedCustomerId.value = sData.default_customer;
        } else {
          selectedCustomerId.value = null;
        }

        if (billers.value.length > 0) {
          selectedBillerId.value = billers.value[0].id!;
        }
      } catch (error) {
        console.error("Error fetching POS initial data:", error);
        promotions.value = [];
        stateStore.setActivePromotion(null);
      }
    };

    const loadSaleForEdit = async () => {
      const saleId = Number(route.params.id);
      if (!saleId) {
        editingSaleId.value = null;
        return;
      }

      try {
        const sale = await posService.getSale(saleId);
        editingSaleId.value = sale.id;
        referenceNo.value = sale.reference_no || "";
        selectedCustomerId.value = sale.customer_id ? Number(sale.customer_id) : null;
        selectedBillerId.value = sale.biller_id ? Number(sale.biller_id) : null;
        note.value = sale.note || "";
        discount.value = Number(sale.discount || 0);
        discountType.value = "fixed";
        selectedMembershipCardId.value = sale.membership_card_id
          ? Number(sale.membership_card_id)
          : null;
        couponCode.value = "";
        couponDiscount.value = Number(sale.coupon_discount || 0);
        couponId.value = sale.coupon_id ? Number(sale.coupon_id) : null;
        taxRate.value = 0;
        shippingCost.value = Number(sale.shipping || 0);
        cashReceived.value = Number(sale.paid || 0);
        manualDiscount.value = discount.value;
        manualDiscountType.value = "fixed";
        stateStore.setOrderDiscount(discount.value, discountType.value as "fixed" | "percentage");
        stateStore.setOrderTax(taxRate.value, "percentage");
        stateStore.setShippingCost(shippingCost.value);
        stateStore.setCoupon(null);
        stateStore.setActivePromotion(null);

        if (sale.warehouse_id && (stateStore as any).setWarehouse) {
          (stateStore as any).setWarehouse(
            Number(sale.warehouse_id),
            sale.warehouse_name || sale.warehouse?.name || ""
          );
        }

        stateStore.selectedProducts.splice(0, stateStore.selectedProducts.length);

        const products = await Promise.all(
          (sale.items || []).map(async (item: any) => {
            try {
              const product = await productService.getProduct(item.product_id);
              return {
                id: product.id!,
                code: product.code,
                name: product.name,
                price: Number(item.unit_price ?? item.price ?? product.price ?? 0),
                qty: Number(item.quantity) || 1,
                stock: getWarehouseStock(product, Number(sale.warehouse_id)) + Number(item.quantity || 0),
                type: product.type,
                discount: Number(item.discount || 0),
                discount_type: normalizeDiscountType(item.discount_type),
                size_id: item.size_id || null,
                sugar_id: item.sugar_id || null,
                product_sizes: product.product_sizes || [],
              };
            } catch {
              return {
                id: item.product_id,
                code: item.product_code,
                name: item.product_name,
                price: Number(item.unit_price ?? item.price ?? 0),
                qty: Number(item.quantity) || 1,
                stock: Number(item.quantity) || 1,
                type: 1,
                discount: Number(item.discount || 0),
                discount_type: normalizeDiscountType(item.discount_type),
                size_id: item.size_id || null,
                sugar_id: item.sugar_id || null,
                product_sizes: [],
              };
            }
          })
        );

        stateStore.selectedProducts.push(...products);
      } catch (error: any) {
        showError(
          error.response?.data?.message ||
            t("pos.unable_to_load_pos_sale_for_edit")
        );
        router.replace("/pos");
      }
    };

    const totalItems = computed(() => {
      return stateStore.selectedProducts.reduce((sum, p) => sum + p.qty, 0);
    });

    const subTotal = computed(() => {
      return stateStore.selectedProducts.reduce(
        (sum, product) => sum + getProductLineTotal(product),
        0
      );
    });

    const taxableAmount = computed(() => {
      let total = subTotal.value;
      // Apply Discount
      let discountAmount = 0;
      if (discountType.value === 'percentage') {
        discountAmount = total * (discount.value / 100);
      } else {
        discountAmount = Math.min(total, Number(discount.value || 0));
      }
      total -= discountAmount;
      // Apply Coupon
      total -= couponDiscount.value;
      return Math.max(0, total);
    });

    const orderTax = computed(() => {
      return taxableAmount.value * (taxRate.value / 100);
    });

    const totalCost = computed(() => {
      let total = taxableAmount.value;
      total += orderTax.value;
      // Apply Shipping
      total += shippingCost.value;
      return Math.max(0, total);
    });

    const directDiscountAmount = computed(() => {
      if (discountType.value === "percentage") {
        return subTotal.value * (discount.value / 100);
      }

      return Math.min(subTotal.value, Number(discount.value || 0));
    });

    const activePromotion = computed(() => stateStore.activePromotion);
    const activePromotionValueLabel = computed(() =>
      activePromotion.value ? formatPromotionValue(activePromotion.value) : ""
    );
    const activePromotionSummary = computed(() => {
      const promotion = activePromotion.value;
      if (!promotion) return "";

      return promotion.customer_name
        ? t("pos.promotion_customer_summary", {
            customer: promotion.customer_name,
          })
        : t("pos.matching_customers_summary");
    });

    const buildCustomerDisplayPromotion = (
      promotion: ActivePromotion | null
    ): CustomerDisplayPromotion | null => {
      if (!promotion) return null;

      return {
        code: promotion.code,
        name: promotion.name,
        description: promotion.description || "",
        value: Number(promotion.value || 0),
        discount_type: promotion.discount_type,
        applied_amount: Number(promotion.applied_amount || 0),
        customer_name: promotion.customer_name || null,
        valid_from: promotion.valid_from || null,
        valid_till: promotion.valid_till || null,
        valid_day: promotion.valid_day || null,
        is_customer_specific: promotion.is_customer_specific,
      };
    };

    const buildCustomerDisplayItems = (): CustomerDisplayLineItem[] =>
      stateStore.selectedProducts.map((product) => ({
        id: product.id,
        name: product.name,
        quantity: Number(product.qty || 0),
        unit_price: Number(product.price || 0),
        base_total: calculateLineBaseTotal(
          Number(product.price || 0),
          Number(product.qty || 0)
        ),
        discount_amount: getProductDiscountAmount(product),
        discount_value: Number(product.discount || 0),
        discount_type: normalizeDiscountType(product.discount_type),
        size_name: resolveSelectedSizeName({
          size_id: product.size_id,
          product_sizes: product.product_sizes,
        }),
        sugar_name: resolveSelectedSugarName({
          sugar_id: product.sugar_id,
        }),
        total: getProductLineTotal(product),
      }));

    const buildCustomerDisplayOrder = (): CustomerDisplayOrderSnapshot | null => {
      const items = buildCustomerDisplayItems();

      if (!items.length) {
        return null;
      }

      const customerName =
        customers.value.find((customer) => customer.id === selectedCustomerId.value)
          ?.name || t("pos.walk_in_customer");

      return {
        reference_no: referenceNo.value || t("pos.current_order"),
        customer_name: customerName,
        warehouse_name:
          stateStore.selectedWarehouseName || t("pos.main_warehouse"),
        payment_method: pendingPaymentMethod.value || "",
        items_count: totalItems.value,
        subtotal: subTotal.value,
        discount: Math.max(0, directDiscountAmount.value),
        coupon_discount: Math.max(0, Number(couponDiscount.value || 0)),
        tax: Math.max(0, Number(orderTax.value || 0)),
        shipping: Math.max(0, Number(shippingCost.value || 0)),
        grand_total: Math.max(0, Number(totalCost.value || 0)),
        amount_received: Math.max(0, Number(cashReceived.value || 0)),
        change_amount: Math.max(0, Number(changeAmount.value || 0)),
        items,
        promotion: buildCustomerDisplayPromotion(activePromotion.value),
      };
    };

    const syncCustomerDisplay = () => {
      const order = buildCustomerDisplayOrder();
      const mode: "idle" | "order" = order ? "order" : "idle";

      writeCustomerDisplayState({
        version: 1,
        mode,
        updated_at: new Date().toISOString(),
        branding: {
          site_name: stateStore.siteName || "Cafe POS",
          logo: stateStore.logo || "",
          company_phone: stateStore.companyPhone || "",
          company_address: stateStore.companyAddress || "",
          company_email: stateStore.companyEmail || "",
          note_to_customer:
            stateStore.receiptSettings.noteToCustomer ||
            t("pos.thanks_for_visiting"),
          currency_symbol: stateStore.currencySymbol || "$",
          posters: stateStore.customerDisplayPosters || [],
        },
        order,
        bill: null,
      });
    };

    const escapeHtml = (value?: string | number | null) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const buildPrintableInvoiceMarkup = () => {
      const sale = lastSaleData.value;
      if (!sale) return "";

      const receiptSettings = stateStore.receiptSettings;
      const companyAddressRow =
        receiptSettings.showAddress && stateStore.companyAddress
          ? `<p class="company-meta">${escapeHtml(stateStore.companyAddress)}</p>`
          : "";
      const companyPhoneRow =
        receiptSettings.showPhone && stateStore.companyPhone
          ? `<p class="company-meta"> ${escapeHtml(stateStore.companyPhone)}</p>`
          : "";
      const companyEmailRow =
        receiptSettings.showEmail && stateStore.companyEmail
          ? `<p class="company-meta">${escapeHtml(stateStore.companyEmail)}</p>`
          : "";
      const customerInfo = receiptSettings.showCustomer
        ? `<div><span class="label">${escapeHtml(t("pos.inv_customer"))}:</span> </div> 
          <div>${escapeHtml(
            sale.customer_name || t("pos.walk_in_customer")
          )}</div>`
        : "";
      const barcodeRow = receiptSettings.showBarcode
        ? `<div class="barcode-ref">${escapeHtml(sale.reference_no)}</div>`
        : "";
      const queueLabel = getBillQueueLabel(sale);
      const queueRow = queueLabel
        ? `
            <div><span class="label">${escapeHtml(
              t("pos.inv_waiting_no")
            )}:</span></div>
            <div>
              #${escapeHtml(queueLabel)}
            </div>
          `
        : "";
      const queueTicketRow = queueLabel
        ? `<div class="barcode-ref">
            #${escapeHtml(queueLabel)}
          </div>`
        : "";
      const noteRow = receiptSettings.showNoteToCustomer
        ? `<p class="closing-note">${escapeHtml(
            receiptSettings.noteToCustomer || t("pos.thanks_for_visiting")
          )}</p>`
        : "";

      const itemRows = (sale.items || [])
        .map(
          (item) => `
            <tr>
              <td>${escapeHtml(item.product_name)}</td>
              <td class="text-center">${escapeHtml(item.quantity)}</td>
              <td class="text-end">${stateStore.formatPrice(Number(item.unit_price || 0))}</td>
              <td class="text-end">${stateStore.formatPrice(Number(item.total || 0))}</td>
            </tr>
          `
        )
        .join("");

      const directDiscount = getDirectDiscount(sale);

      const discountRow =
        receiptSettings.showTaxDiscount && directDiscount > 0
          ? `
            <div class="totals-row">
              <span class="label">${escapeHtml(t("pos.inv_discount"))}:</span>
              <span class="value text-danger">-${stateStore.formatPrice(directDiscount)}</span>
            </div>
          `
          : "";

      const couponRow =
        receiptSettings.showTaxDiscount && Number(sale.coupon_discount || 0) > 0
          ? `
            <div class="totals-row">
              <span class="label">${escapeHtml(t("pos.inv_coupon"))}:</span>
              <span class="value text-danger">-${stateStore.formatPrice(
                Number(sale.coupon_discount || 0)
              )}</span>
            </div>
          `
          : "";

      const taxRow =
        receiptSettings.showTaxDiscount && Number(sale.tax || 0) > 0
          ? `
            <div class="totals-row">
              <span class="label">${escapeHtml(t("pos.inv_order_tax"))}:</span>
              <span class="value">${stateStore.formatPrice(Number(sale.tax || 0))}</span>
            </div>
          `
          : "";

      const shippingRow =
        receiptSettings.showTaxDiscount && Number(sale.shipping || 0) > 0
          ? `
            <div class="totals-row">
              <span class="label">${escapeHtml(t("pos.inv_shipping"))}:</span>
              <span class="value">${stateStore.formatPrice(Number(sale.shipping || 0))}</span>
            </div>
          `
          : "";

      const membershipCardRow =
        String(sale.membership_card_number || "").trim() &&
        String(sale.paid_by || "").toLowerCase() === "card"
          ? `
            <div class="totals-row">
              <span class="label">${escapeHtml(t("pos.inv_membership_card"))}:</span>
              <span class="value">${escapeHtml(sale.membership_card_number)}</span>
            </div>
          `
          : "";

      return `
        <html>
          <head>
            <title>${escapeHtml(t("pos.receipt"))} ${escapeHtml(
              sale.reference_no
            )}</title>
            <style>
              @page {
                size: auto;
                margin: 10mm;
              }
              body {
                margin: 0;
                padding: 24px;
                background: #ffffff;
                color: #111827;
                font-family: Arial, sans-serif;
              }
              .invoice-shell {
                max-width: 500px;
                margin: 0 auto;
                border-radius: 16px;
                background: #ffffff;
                overflow: hidden;
              }
              .invoice-body {
                padding: 24px;
              }
              .text-center {
                text-align: center;
              }
              .text-end {
                text-align: right;
              }
              .company-name {
                margin: 0 0 6px;
                font-size: 28px;
                font-weight: 700;
                color: #111827;
              }
              .company-meta {
                margin: 0;
                font-size: 13px;
                font-weight: 700;
                color: #374151;
              }
              .company-meta + .company-meta {
                margin-top: 2px;
              }
              .summary-grid {
                display: flex;
                justify-content: space-between;
                gap: 16px;
                font-size: 13px;
                line-height: 1.7;
              }
              .label {
                font-weight: 700;
              }
              .barcode-ref {
                margin-bottom: 12px;
                text-align: center;
                font-size: 30px;
                font-weight: 600;
                color: #6b7280;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 16px;
                font-size: 13px;
              }
              thead th {
                padding: 10px 0;
                border-top: 1px solid #111827;
                border-bottom: 1px solid #111827;
                color: #111827;
              }
              tbody td {
                padding: 10px 0;
                border-bottom: 1px dashed #d1d5db;
              }
              .totals {
                padding-top: 8px;
              }
              .totals-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 12px;
                margin-bottom: 8px;
                font-size: 13px;
              }
              .totals-row.grand-total {
                margin-top: 8px;
                padding-top: 10px;
                border-top: 1px solid #111827;
                font-size: 18px;
                font-weight: 700;
              }
              .value {
                font-weight: 700;
              }
              .text-danger {
                color: #dc2626;
              }
              .footer {
                margin-top: 24px;
                padding-top: 16px;
                border-top: 1px solid #d1d5db;
                text-align: center;
                font-size: 12px;
              }
              .closing-note {
                margin: 0 0 6px;
                font-size: 13px;
                font-weight: 700;
                color: #111827;
              }
              .powered-by {
                margin: 0;
                color: #6b7280;
              }
            </style>
          </head>
          <body>
            <div class="invoice-shell">
              <div class="invoice-body">
                <div class="text-center">
                  <h1 class="company-name">${escapeHtml(stateStore.siteName)}</h1>
                  ${companyAddressRow}
                  ${companyPhoneRow}
                  ${companyEmailRow}
                </div>

                <div>
                  <div class="summary-grid">
                    <div>
                      <span class="label">${escapeHtml(
                        t("pos.inv_date")
                      )}:</span> 
                    </div>
                    <div>
                      ${escapeHtml(sale.date)}
                    </div>
                  </div>
                  <div class="summary-grid">
                    <div>
                      <span class="label">${escapeHtml(
                        t("pos.inv_reference")
                      )}:</span>
                    </div>
                    <div>
                      ${escapeHtml(sale.reference_no)}
                    </div>
                  </div>
                  <div class="summary-grid">
                    ${customerInfo}
                  </div>
                  <div class="summary-grid">
                    <div>
                      <span class="label">${escapeHtml(
                        t("pos.inv_warehouse")
                      )}:</span>
                    </div>
                    <div>
                      ${escapeHtml(
                        sale.warehouse_name || "-"
                      )}
                    </div>
                  </div>
                  <div class="summary-grid">
                    ${queueRow}
                  </div>
                </div>

                ${queueTicketRow}
                ${barcodeRow}

                <table>
                  <thead>
                    <tr>
                      <th>
                        មុខទំនិញ <br> Item Name  
                      </th>
                      <th class="text-center">
                        ចំនួន <br> Qty
                      </th>
                      <th class="text-end">
                        តម្លៃ <br> Unit Price
                      </th>
                      <th class="text-end">
                        សរុុប <br> Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>${itemRows}</tbody>
                </table>

                <div class="totals">
                  <div class="totals-row">
                    <span class="label">${escapeHtml(t("pos.inv_subtotal"))}:</span>
                    <span class="value">${stateStore.formatPrice(Number(sale.total || 0))}</span>
                  </div>
                  ${discountRow}
                  ${couponRow}
                  ${taxRow}
                  ${shippingRow}
                  <div class="totals-row grand-total">
                    <span>${escapeHtml(t("pos.inv_grand_total"))}:</span>
                    <span>${stateStore.formatPrice(Number(sale.grand_total || 0))}</span>
                  </div>
                  <div class="totals-row">
                    <span class="label">${escapeHtml(
                      t("pos.inv_amount_received")
                    )}:</span>
                    <span class="value">${stateStore.formatPrice(
                      Number(sale.cash_received || 0)
                    )}</span>
                  </div>
                  <div class="totals-row">
                    <span class="label">${escapeHtml(t("pos.inv_change"))}:</span>
                    <span class="value">${stateStore.formatPrice(
                      Number(sale.change_amount || 0)
                    )}</span>
                  </div>
                  <div class="totals-row">
                    <span class="label">${escapeHtml(
                      t("pos.inv_payment_method")
                    )}:</span>
                    <span class="value">${escapeHtml(formatPaymentMethod(sale.paid_by))}</span>
                  </div>
                  ${membershipCardRow}
                </div>

                <div class="footer">
                  ${noteRow}
                  <p class="text-muted small">Powered by ProCare Digital Marketing</p>
                </div>
              </div>
            </div>
          </body>
        </html>
      `;
    };

    // Modal Methods
    const openDiscountModal = () => {
        if (activePromotion.value) {
          return;
        }
        tempDiscount.value = discount.value;
        tempDiscountType.value = discountType.value;
    }
    const handleDiscountRowClick = () => {
        if (activePromotion.value) {
          showWarning(t("pos.order_discount_controlled"));
          return;
        }

        openDiscountModal();
    };
    const applyDiscount = () => {
        const sanitizedType = tempDiscountType.value === 'percentage' ? 'percentage' : 'fixed';
        const sanitizedDiscount = Math.max(
          0,
          sanitizedType === 'percentage'
            ? Math.min(100, Number(tempDiscount.value) || 0)
            : Number(tempDiscount.value) || 0
        );

        discount.value = sanitizedDiscount;
        discountType.value = sanitizedType;
        manualDiscount.value = sanitizedDiscount;
        manualDiscountType.value = sanitizedType;
        stateStore.setOrderDiscount(sanitizedDiscount, sanitizedType);
    };

    const openCouponModal = () => {
        tempCouponCode.value = couponCode.value;
        couponMessage.value = '';
        isCouponValid.value = null;
    };
    const handleCouponSelection = (event: Event) => {
      const code = (event.target as HTMLSelectElement).value;
      if (code) {
        tempCouponCode.value = code;
        verifyCoupon();
      } else {
        removeCoupon();
      }
    };

    const verifyCoupon = async () => {
        if (!tempCouponCode.value) {
            couponMessage.value = t("pos.please_enter_coupon_code");
            isCouponValid.value = false;
            return;
        }
        try {
            const res = await couponService.verify(tempCouponCode.value, subTotal.value);
            if (res.valid) {
                isCouponValid.value = true;
                couponMessage.value = t("pos.coupon_valid_discount", {
                  discount:
                    res.data.type === "percentage"
                      ? `${res.data.discount}%`
                      : stateStore.formatPrice(Number(res.data.discount || 0)),
                });
                
                // Save coupon data
                couponCode.value = tempCouponCode.value;
                couponId.value = res.data.id;
                stateStore.setCoupon(res.data);
                
                // Calculate discount
                if (res.data.type === 'percentage') {
                    couponDiscount.value = subTotal.value * (res.data.discount / 100);
                } else {
                    couponDiscount.value = Math.min(subTotal.value, parseFloat(res.data.discount));
                }
            } else {
                isCouponValid.value = false;
                couponMessage.value = res.message;
                stateStore.setCoupon(null);
            }
        } catch (error: any) {
            isCouponValid.value = false;
            couponMessage.value =
              error.response?.data?.message || t("pos.invalid_coupon");
            stateStore.setCoupon(null);
        }
    };

    const openTaxModal = () => {
        tempTaxRate.value = taxRate.value;
    };
    const applyTax = () => {
        taxRate.value = Math.max(0, Math.min(100, Number(tempTaxRate.value) || 0));
        stateStore.setOrderTax(taxRate.value, 'percentage');
    };

    const openShippingModal = () => {
        tempShippingCost.value = shippingCost.value;
    };
    const applyShipping = () => {
        shippingCost.value = Math.max(0, Number(tempShippingCost.value) || 0);
        stateStore.setShippingCost(shippingCost.value);
    };

    const removeCoupon = () => {
      couponCode.value = '';
      couponDiscount.value = 0;
      couponId.value = null;
      tempCouponCode.value = '';
      couponMessage.value = '';
      isCouponValid.value = null;
      stateStore.setCoupon(null);
    };

    const handleReset = () => {
      Swal.fire({
        title: t("pos.reset_confirm_title"),
        text: t("pos.reset_confirm_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("pos.reset_confirm_button"),
        cancelButtonText: t("common.cancel")
      }).then((result) => {
        if (result.isConfirmed) {
          stateStore.clearSelectedProducts();
          discount.value = 0;
          discountType.value = 'fixed';
          couponCode.value = '';
          couponDiscount.value = 0;
          couponId.value = null;
          cashReceived.value = 0;
          taxRate.value = 0;
          shippingCost.value = 0;
          stateStore.setOrderDiscount(0, 'fixed');
          stateStore.setOrderTax(0, 'percentage');
          stateStore.setShippingCost(0);
          stateStore.setCoupon(null);
          stateStore.setActivePromotion(null);
          note.value = "";
          pendingPaymentMethod.value = null;
          selectedMembershipCardId.value = null;
          fetchNextReference();
          // Reset customer/biller to defaults
          fetchInitialData();
          
          Swal.fire({
            title: t("pos.reset_done_title"),
            text: t("pos.reset_done_text"),
            icon: "success",
            timer: 1500,
            showConfirmButton: false
          });
        }
      });
    };

    const cancelCheckout = () => {
      if (pendingPaymentMethod.value !== "cash") {
        cashReceived.value = 0;
      }

      pendingPaymentMethod.value = null;
    };

    const prepareCheckout = (paymentMethod: string) => {
      if (stateStore.selectedProducts.length === 0) {
        // We can't easily prevent the modal from opening with data-bs-target 
        // if we also want to show this alert. But we can handle it in the modal.
        return;
      }
      if (paymentMethod === "card" && !canUseMembershipCard) {
        showWarning(t("pos.no_membership_card_permission"));
        return;
      }
      if (!stateStore.selectedWarehouseId) {
        showWarning(t("pos.please_select_warehouse_first"));
        return;
      }
      pendingPaymentMethod.value = paymentMethod;
      if (paymentMethod !== 'cash') {
        cashReceived.value = totalCost.value;
      }
      if (paymentMethod === "card") {
        const eligibleCards = availableMembershipCards.value;
        if (
          !selectedMembershipCard.value ||
          !eligibleCards.some(
            (card) => Number(card.id || 0) === Number(selectedMembershipCardId.value || 0)
          )
        ) {
          selectedMembershipCardId.value =
            eligibleCards.length === 1 ? Number(eligibleCards[0].id || 0) : null;
        }
      }
    };

    const confirmCheckout = async () => {
      if (!pendingPaymentMethod.value || isSubmitting.value) return;
      // Safety check: Ensure register is open
      if (!stateStore.registerStatus.isOpen) {
        showWarning(t("pos.please_open_register_before_sale"));
        const openBtn = document.getElementById('triggerOpenRegisterModal');
        if (openBtn) openBtn.click();
        return;
      }
      if (stateStore.selectedProducts.length === 0) {
        showWarning(t("pos.please_add_products_to_cart"));
        return;
      }
      if (pendingPaymentMethod.value === "card" && !canUseMembershipCard) {
        showWarning(t("pos.no_membership_card_permission"));
        return;
      }
      if (pendingPaymentMethod.value === "card" && membershipCardError.value) {
        showWarning(membershipCardError.value);
        return;
      }
      isSubmitting.value = true;
      const saleData: SaleData = {
        customer_id: selectedCustomerId.value || null,
        warehouse_id: stateStore.selectedWarehouseId || 0,
        biller_id: selectedBillerId.value || null,
        reference_no: referenceNo.value,
        items: stateStore.selectedProducts.map(p => ({
          product_id: p.id,
          product_code: p.code,
          product_name: p.name,
          quantity: p.qty,
          unit_price: p.price,
          discount: getProductDiscountAmount(p),
          size_id: p.size_id,
          sugar_id: p.sugar_id,
        })),
        discount: discount.value,
        total_discount: (discountType.value === 'percentage' ? subTotal.value * (discount.value / 100) : discount.value) + couponDiscount.value,
        coupon_id: couponId.value,
        coupon_discount: couponDiscount.value,
        tax: orderTax.value, // Amount
        shipping: shippingCost.value,
        paid: totalCost.value,
        paid_by: pendingPaymentMethod.value,
        membership_card_id:
          pendingPaymentMethod.value === "card"
            ? selectedMembershipCardId.value || null
            : null,
        membership_card_number:
          pendingPaymentMethod.value === "card"
            ? selectedMembershipCard.value?.card_number || null
            : null,
        pos: 1,
        status: 'completed',
        note: note.value,
      };
      try {
        const paymentSnapshot: PosPaymentSnapshot = {
          cash_received: Number(cashReceived.value || 0),
          change_amount: Number(changeAmount.value || 0),
        };
        const wasEditing = !!editingSaleId.value;
        const fallbackBill = buildFallbackBillData(
          saleData,
          paymentSnapshot,
          Number(editingSaleId.value || 0)
        );

        const response = wasEditing && editingSaleId.value
          ? await posService.updateSale(editingSaleId.value, saleData)
          : await posService.createSale(saleData);

        const savedSaleId = Number(response?.sale_id || editingSaleId.value || 0);
        const queueAwareFallbackBill: PosBillData = {
          ...fallbackBill,
          queue_number: Number(response?.queue_number || fallbackBill.queue_number || 0) || null,
          queue_label: String(
            response?.queue_label ||
              fallbackBill.queue_label ||
              formatQueueLabel(response?.queue_number, "")
          ),
          queue_status: String(
            response?.queue_status || fallbackBill.queue_status || "pending"
          ),
        };

        if (savedSaleId > 0) {
          await loadSavedBillData(savedSaleId, paymentSnapshot, queueAwareFallbackBill);
        } else {
          persistLastBill(queueAwareFallbackBill);
          invoiceLoading.value = false;
        }

        // Hide the confirmation modal
        const closeBtn = document.querySelector('#checkoutConfirmModal .btn-close') as HTMLButtonElement;
        if (closeBtn) closeBtn.click();

        stateStore.clearSelectedProducts();
        cashReceived.value = 0;
        selectedMembershipCardId.value = null;
        stateStore.triggerRefresh();
        editingSaleId.value = null;

        if (wasEditing) {
          await router.replace("/pos");
        }

        await fetchNextReference();

        setTimeout(() => {
          const openInvoiceBtn = document.getElementById('openInvoiceBtn');
          if (openInvoiceBtn) openInvoiceBtn.click();
        }, 100);
        
        // Refresh register details to update summary for Close Register modal
        const res = await registerService.checkRegister();
        currentRegister.value = res.register;
      } catch (error: any) {
        console.error("Checkout failed:", error);
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
        showError(t("pos.checkout_failed", { message: errorMsg }));
      } finally {
        isSubmitting.value = false;
        pendingPaymentMethod.value = null;
      }
    };
    const printInvoice = () => {
      const printMarkup = buildPrintableInvoiceMarkup();
      if (!printMarkup) {
        showWarning(t("pos.no_invoice_available_to_print"));
        return;
      }

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const iframeWindow = iframe.contentWindow;
      const iframeDocument = iframe.contentDocument || iframeWindow?.document;

      if (!iframeWindow || !iframeDocument) {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        showError(t("pos.unable_to_prepare_invoice_for_print"));
        return;
      }

      iframeDocument.open();
      iframeDocument.write(printMarkup);
      iframeDocument.close();

      let hasPrinted = false;
      const cleanup = () => {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 300);
      };

      const triggerPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        iframeWindow.focus();
        iframeWindow.print();
        iframeWindow.onafterprint = cleanup;
        setTimeout(cleanup, 1500);
      };

      iframe.onload = () => setTimeout(triggerPrint, 250);
      setTimeout(triggerPrint, 600);
    };

    watch(
      () => [discount.value, discountType.value, activePromotion.value] as const,
      ([discountValue, discountMode, promotion]) => {
        if (!promotion) {
          manualDiscount.value = Number(discountValue || 0);
          manualDiscountType.value = discountMode as "fixed" | "percentage";
        }
      },
      { immediate: true }
    );

    watch(
      [promotions, selectedCustomerId, subTotal, editingSaleId],
      ([promotionList, customerId, subtotal, saleId]) => {
        if (saleId) {
          stateStore.setActivePromotion(null);
          return;
        }

        const nextPromotion = resolveBestPromotion(promotionList, {
          customerId,
          subtotal,
        });

        if (nextPromotion) {
          stateStore.setActivePromotion(nextPromotion);
          discount.value = Number(nextPromotion.value || 0);
          discountType.value = nextPromotion.discount_type;
          stateStore.setOrderDiscount(
            Number(nextPromotion.value || 0),
            nextPromotion.discount_type
          );
          return;
        }

        if (stateStore.activePromotion) {
          discount.value = manualDiscount.value;
          discountType.value = manualDiscountType.value;
          stateStore.setOrderDiscount(
            manualDiscount.value,
            manualDiscountType.value
          );
        }

        stateStore.setActivePromotion(null);
      },
      { immediate: true }
    );

    watch(selectedCustomerId, (customerId) => {
      if (!selectedMembershipCard.value) {
        return;
      }

      const selectedCardCustomerId = Number(
        selectedMembershipCard.value.customer_id || 0
      );

      if (
        customerId &&
        selectedCardCustomerId &&
        selectedCardCustomerId !== Number(customerId)
      ) {
        selectedMembershipCardId.value = null;
      }
    });

    watch(selectedMembershipCardId, (cardId) => {
      const card =
        membershipCards.value.find(
          (item) => Number(item.id || 0) === Number(cardId || 0)
        ) || null;

      if (!card) {
        return;
      }

      const cardCustomerId = Number(card.customer_id || 0);
      if (
        cardCustomerId &&
        Number(selectedCustomerId.value || 0) !== cardCustomerId
      ) {
        selectedCustomerId.value = cardCustomerId;
      }
    });

    watch(
      [pendingPaymentMethod, totalCost],
      ([paymentMethod, total]) => {
        if (paymentMethod && paymentMethod !== "cash") {
          cashReceived.value = Number(total || 0);
        }
      },
      { immediate: true }
    );

    watchEffect(() => {
      syncCustomerDisplay();
    });

    onMounted(async () => {
      restoreLastBill();
      await fetchInitialData();
      await loadSaleForEdit();
    });

    return {
      stateStore,
      subTotal,
      coupons,
      handleCouponSelection,
      customers,
      billers,
      membershipCards,
      canUseMembershipCard,
      availableMembershipCards,
      membershipCardOptions,
      selectedMembershipCardId,
      selectedMembershipCardValue,
      selectedMembershipCard,
      membershipCardRemainingBalance,
      membershipCardError,
      getMembershipCardPopupContainer,
      editingSaleId,
      referenceNo,
      selectedCustomerId,
      selectedBillerId,
      totalItems,
      totalCost,
      getProductLineTotal,
      getProductPromotionLabel,
      activePromotion,
      activePromotionSummary,
      activePromotionValueLabel,
      prepareCheckout,
      cancelCheckout,
      confirmCheckout,
      pendingPaymentMethod,
      activePaymentMethodLabel,
      isCheckoutDisabled,
      isSubmitting,
      invoiceLoading,
      lastSaleData,
      formatStatusLabel,
      getBillTone,
      formatPaymentMethod,
      getBillItemsCount,
      getBillQueueLabel,
      getDirectDiscount,
      printInvoice,
      note,
      openingBalance,
      closingBalance,
      closeNote,
      isRegisterSubmitting,
      handleOpenRegister,
      cancelOpenRegister,
      handleCloseRegister,
      currentRegister,
      registerHistory,
      suspendedSales,
      handleSuspend,
      fetchSuspendedSales,
      resumeSuspend,
      discount,
      discountType,
      couponCode,
      couponDiscount,
      taxRate,
      shippingCost,
      orderTax,
      tempDiscount,
      tempDiscountType,
      tempCouponCode,
      tempTaxRate,
      tempShippingCost,
      couponMessage,
      isCouponValid,
      handleDiscountRowClick,
      openDiscountModal,
      applyDiscount,
      openCouponModal,
      verifyCoupon,
      removeCoupon,
      openTaxModal,
      applyTax,
      openShippingModal,
      applyShipping,
      handleReset,
      cashReceived,
      changeAmount,
    };
  },
});
</script>

<style lang="scss" scoped>
.pos-right {
  width: calc(40% - 10px);
  background: white;
  min-height: 100vh;
  border-left: 1px solid #f1f5f9;
  padding-top: 10px;

  .dropdown-toggle-container:hover {
    opacity: 0.8;
  }

  .promotion-auto-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 18px;
    border: 1px solid #dbeafe;
    background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%);
    color: #1d4ed8;

    strong,
    small,
    span {
      display: block;
    }

    strong {
      font-size: 15px;
      color: #1e3a8a;
    }

    small {
      font-size: 12px;
      color: #475569;
    }
  }

  .promotion-auto-kicker {
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #2563eb;
  }

  .promotion-auto-value {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 800;
    color: #1d4ed8;
  }

  .membership-card-panel {
    padding: 16px;
    border: 1px solid #dbe4f0;
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  }

  .membership-card-select {
    width: 100%;
  }

  .membership-card-select :deep(.ant-select-selector) {
    min-height: 52px !important;
    border-radius: 16px !important;
    border: 1px solid #d7def4 !important;
    box-shadow: none !important;
    font-weight: 600;
    padding: 9px 14px !important;
    background: #ffffff !important;
    align-items: center;
  }

  .membership-card-select :deep(.ant-select-selection-item),
  .membership-card-select :deep(.ant-select-selection-placeholder),
  .membership-card-select :deep(.ant-select-selection-search-input) {
    color: #0f172a !important;
    font-weight: 600;
  }

  .membership-card-select :deep(.ant-select-selection-placeholder) {
    color: #64748b !important;
  }

  .membership-card-select :deep(.ant-select-arrow) {
    color: #64748b;
  }

  .membership-card-select.ant-select-focused :deep(.ant-select-selector),
  .membership-card-select:hover :deep(.ant-select-selector) {
    border-color: #605dff !important;
    box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12) !important;
  }

  .membership-card-summary {
    display: grid;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 18px;
    background: #eef4ff;
  }

  .membership-card-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: #475569;
  }

  .membership-card-summary-row strong {
    color: #0f172a;
  }

  .register-balance-prefix {
    border: 1px solid #cbd5e1;
    border-right: 0;
    background: #f8fafc;
    min-width: 48px;
    justify-content: center;
  }

  .register-balance-prefix-primary {
    color: #2563eb;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .register-balance-prefix-danger {
    color: #dc2626;
    background: #fef2f2;
    border-color: #fca5a5;
  }

  .register-balance-input {
    border: 1px solid #cbd5e1 !important;
    border-left: 0 !important;
    background: #ffffff;
    color: #0f172a;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

    &::placeholder {
      color: #94a3b8;
    }

    &:focus {
      border-color: #93c5fd !important;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
    }
  }

  .register-balance-input-danger {
    &:focus {
      border-color: #fca5a5 !important;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
    }
  }

  .locked-discount-row {
    border-radius: 12px;
    padding: 4px 8px;
    background: #f8fbff;
  }

  .auto-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 999px;
    background: #dbeafe;
    color: #2563eb;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
  }
  
  .payment-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  
  .payment-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 8px;
    transition: all 0.2s;
    
    span {
      font-size: 11px;
      font-weight: 700;
      color: #1e293b;
      margin-top: 2px;
    }
    
    &:hover {
      border-color: #0d6efd;
      background: #f8fafc;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    }
  }

  .action-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .bill-launch-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    border: 1px solid #cfe2ff;
    border-radius: 22px;
    background: linear-gradient(135deg, #eef5ff 0%, #f8fbff 100%);
    color: #0f172a;
    padding: 14px 16px;
    text-align: left;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      border-color: #0d6efd;
      transform: translateY(-1px);
      box-shadow: 0 12px 24px rgba(13, 110, 253, 0.12);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      box-shadow: none;
    }
  }

  .bill-launch-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;

    strong {
      font-size: 15px;
      line-height: 1.2;
    }

    small {
      color: #64748b;
      font-size: 12px;
      margin-top: 4px;
      white-space: normal;
    }
  }
  
  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 20px;
    padding: 10px 15px;
    font-size: 14px;
    font-weight: 700;
    transition: all 0.2s;
    
    &:hover {
      opacity: 0.85;
      transform: translateY(-1px);
    }
  }
}

.bill-modal-content {
  border-radius: 28px;
  overflow: hidden;
}

.bill-modal-header {
  padding: 24px 28px 18px;
  background:
    radial-gradient(circle at top left, rgba(13, 110, 253, 0.14), transparent 42%),
    linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.bill-modal-eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.bill-modal-caption {
  color: #64748b;
  font-size: 14px;
}

.bill-modal-body {
  background: #f4f7fb;
}

.bill-loading-state,
.bill-empty-state {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
}

.bill-empty-icon {
  width: 72px;
  height: 72px;
  border-radius: 24px;
  display: grid;
  place-items: center;
  background: rgba(13, 110, 253, 0.08);
  color: #0d6efd;
  font-size: 30px;
  margin-bottom: 16px;
}

.bill-summary-column {
  background:
    linear-gradient(160deg, #0f172a 0%, #12284a 40%, #173b74 100%);
  color: #ffffff;
}

.bill-summary-wrap {
  padding: 28px;
  height: 100%;
}

.bill-summary-hero {
  margin-bottom: 22px;
}

.bill-summary-label {
  display: block;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 8px;
}

.bill-summary-hero h3 {
  font-size: 28px;
  line-height: 1.1;
  margin: 0;
}

.bill-summary-hero p {
  color: rgba(255, 255, 255, 0.72);
  font-size: 14px;
}

.bill-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.bill-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}

.bill-chip.is-success {
  background: rgba(34, 197, 94, 0.18);
  color: #dcfce7;
}

.bill-chip.is-warning {
  background: rgba(245, 158, 11, 0.18);
  color: #fef3c7;
}

.bill-chip.is-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #fee2e2;
}

.bill-chip.is-neutral {
  background: rgba(148, 163, 184, 0.18);
  color: #e2e8f0;
}

.bill-chip-outline {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.18);
}

.bill-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.bill-stat-card,
.bill-detail-card,
.bill-note-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  border-radius: 20px;
}

.bill-stat-card {
  padding: 16px;
}

.bill-stat-title,
.bill-note-label {
  display: block;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  margin-bottom: 8px;
}

.bill-stat-card strong {
  font-size: 20px;
  line-height: 1.2;
}

.bill-detail-card {
  padding: 14px 18px;
  margin-bottom: 18px;
}

.bill-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 0;
  font-size: 14px;
}

.bill-detail-row + .bill-detail-row {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.bill-detail-row span {
  color: rgba(255, 255, 255, 0.62);
}

.bill-detail-row strong {
  text-align: right;
}

.bill-note-card {
  padding: 16px 18px;
}

.bill-note-card p {
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
}

.bill-preview-column {
  background: #eff4fb;
}

.bill-preview-shell {
  padding: 28px;
  min-height: 100%;
}

.bill-paper {
  position: relative;
  max-width: 560px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
  padding: 28px;
  overflow: hidden;
}

.bill-paper-accent {
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(90deg, #0d6efd 0%, #38bdf8 50%, #22c55e 100%);
}

.bill-paper-header {
  text-align: center;
  color: #0f172a;
  padding-top: 8px;
}

.bill-paper-kicker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(13, 110, 253, 0.08);
  color: #0d6efd;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.bill-paper-header h2 {
  font-size: 30px;
  margin-bottom: 6px;
}

.bill-paper-header p {
  color: #475569;
  font-size: 13px;
}

.bill-paper-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 22px 0 14px;
}

.bill-paper-meta-card {
  padding: 16px;
  border-radius: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #0f172a;
}

.bill-paper-meta-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 8px;
}

.bill-paper-meta-card strong {
  display: block;
  font-size: 16px;
  margin-bottom: 4px;
}

.bill-paper-meta-card small {
  color: #64748b;
  font-size: 12px;
}

.bill-paper-reference {
  text-align: center;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 14px;
}

.bill-paper-table {
  width: 100%;
  border-collapse: collapse;
  color: #0f172a;
}

.bill-paper-table thead th {
  padding: 12px 0;
  border-top: 1px solid #0f172a;
  border-bottom: 1px solid #0f172a;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bill-paper-table tbody td {
  padding: 12px 0;
  border-bottom: 1px dashed #dbe3ee;
  font-size: 14px;
}

.bill-paper-totals {
  padding-top: 14px;
}

.bill-paper-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  color: #0f172a;
  font-size: 14px;
}

.bill-paper-total-row strong {
  font-weight: 700;
}

.bill-paper-total-row.is-grand {
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid #0f172a;
  font-size: 18px;
}

.bill-paper-footer {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #dbe3ee;
  text-align: center;
}

.bill-paper-note {
  margin-bottom: 6px;
  font-weight: 700;
  color: #0f172a;
}

.bill-paper-powered {
  color: #64748b;
  font-size: 12px;
}

.bill-modal-footer {
  padding: 18px 28px 24px;
  background: #ffffff;
}

.bill-print-btn {
  min-width: 148px;
}

@media only screen and (max-width: 575px) {
  .pos-right {
    .payment-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .action-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media only screen and (max-width: 991px) {
  .pos-right {
    width: 100%;
  }

  .bill-stat-grid,
  .bill-paper-meta {
    grid-template-columns: 1fr;
  }

  .bill-summary-wrap,
  .bill-preview-shell {
    padding: 20px;
  }

  .bill-paper {
    padding: 20px;
  }
}

@media only screen and (max-width: 767px) {
  .bill-modal-header,
  .bill-modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .bill-detail-row,
  .bill-paper-total-row {
    font-size: 13px;
  }
}

@media only screen and (min-width: 1600px) {
  .pos-right {
    width: calc(35% - 10px);
  }
}
</style>
